import type { BundleInfo, ReplayJson, ShareStateTimingAgg, ShareStateTimingSample } from './app/types';
import { $, setMeta, showStatus, updateProgress } from './app/dom';
import { formatDuration, percentile } from './app/format';
import { extractReplayId, parseJoinReplayIdFromHash, replayKeyForId, setJoinReplayIdHash } from './app/hashRoute';
import {
  applyCardLayoutToGrid,
  cardLayoutStorageKey,
  closeAvailablePopover,
  decorateCardEl,
  initCardGridInteractions,
  openAvailablePopover,
  refreshAvailablePopover,
  slugifyCardId,
  updateAvailableButton,
} from './app/cards/layout';
import { statCard } from './app/cards/card';

// Make this file a module so it doesn't pollute the global scope (avoids TS redeclare errors
// when generated JS assets are also present in the workspace).
export {};

let currentReplay: ReplayJson | null = null;
let analysisWorker: Worker | null = null;
let analyzerCommit: string = 'dev';
let lastComputedStats: any = null;
let lastBundleInfo: BundleInfo | null = null;

let currentReplayId: string | null = null;
let currentReplayKey: string | null = null;
let currentContentHash: string | null = null;
let currentTimingSamples: ShareStateTimingSample[] = [];
let currentTimingAgg: ShareStateTimingAgg | null = null;
let hashJoinLoadInFlight = false;

type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'ofra.theme';

function readStoredTheme(): ThemeMode | null {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: ThemeMode) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore (storage may be disabled)
  }
}

function getEffectiveTheme(mql: MediaQueryList): ThemeMode {
  const override = document.documentElement.dataset.theme;
  if (override === 'light' || override === 'dark') return override;
  const stored = readStoredTheme();
  if (stored) return stored;
  return mql.matches ? 'dark' : 'light';
}

function setupThemeToggle() {
  const btn = document.getElementById('themeToggle') as HTMLButtonElement | null;
  if (!btn) return;

  const mql = window.matchMedia('(prefers-color-scheme: dark)');

  const stored = readStoredTheme();
  if (stored) document.documentElement.dataset.theme = stored;

  const updateBtn = () => {
    const t = getEffectiveTheme(mql);
    const isDark = t === 'dark';
    btn.setAttribute('aria-pressed', isDark ? 'true' : 'false');
    btn.title = isDark ? 'Switch to light mode' : 'Switch to dark mode';
    btn.setAttribute('aria-label', btn.title);
  };

  btn.addEventListener('click', () => {
    const current = getEffectiveTheme(mql);
    const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    writeStoredTheme(next);
    updateBtn();
  });

  // Keep the toggle label in sync when following the system theme.
  mql.addEventListener('change', updateBtn);
  updateBtn();
}

type ChartVariant = 'line' | 'stacked-area' | 'stacked-area-100';

function chartVariantStorageKey(chartId: string) {
  return `ofra.chartVariant.${chartId}`;
}

function chartLegendStorageKey(chartId: string) {
  return `ofra.chartLegend.${chartId}`;
}

function readStoredChartVariant(chartId: string): ChartVariant | null {
  try {
    const raw = localStorage.getItem(chartVariantStorageKey(chartId));
    return raw === 'line' || raw === 'stacked-area' || raw === 'stacked-area-100' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredChartVariant(chartId: string, variant: ChartVariant) {
  try {
    localStorage.setItem(chartVariantStorageKey(chartId), variant);
  } catch {
    // ignore (storage may be disabled)
  }
}

function readStoredChartLegend(chartId: string): boolean | null {
  try {
    const raw = localStorage.getItem(chartLegendStorageKey(chartId));
    if (raw === null) return null;
    return raw === '1' || raw === 'true';
  } catch {
    return null;
  }
}

function writeStoredChartLegend(chartId: string, visible: boolean) {
  try {
    localStorage.setItem(chartLegendStorageKey(chartId), visible ? '1' : '0');
  } catch {
    // ignore (storage may be disabled)
  }
}

function defaultChartVariant(chartId: string): ChartVariant {
  // Start with a few sensible defaults; everything else stays a line chart.
  if (chartId === 'tiles-owned') return 'stacked-area-100';
  if (chartId === 'troops') return 'stacked-area';
  if (chartId === 'conquests') return 'stacked-area-100';
  return 'line';
}

function computeTimingAgg(samples: ShareStateTimingSample[]): ShareStateTimingAgg {
  const nums = (key: keyof ShareStateTimingSample) =>
    samples
      .map((s) => s[key])
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
      .sort((a, b) => a - b);

  const runtime = nums('runtimeMs');
  const avgTick = nums('avgTickExecutionMs');
  const p95 = nums('tickP95Ms');
  const max = nums('tickMaxMs');

  const created = samples
    .map((s) => s.createdAt)
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .slice()
    .sort();

  return {
    samples: samples.length,
    firstCreatedAt: created[0] ?? null,
    lastCreatedAt: created.length ? created[created.length - 1] : null,
    runtimeMs: { min: runtime[0] ?? null, p50: percentile(runtime, 0.5), p95: percentile(runtime, 0.95), max: runtime.at(-1) ?? null },
    avgTickExecutionMs: { min: avgTick[0] ?? null, p50: percentile(avgTick, 0.5), p95: percentile(avgTick, 0.95), max: avgTick.at(-1) ?? null },
    tickP95Ms: { min: p95[0] ?? null, p50: percentile(p95, 0.5), p95: percentile(p95, 0.95), max: p95.at(-1) ?? null },
    tickMaxMs: { min: max[0] ?? null, p50: percentile(max, 0.5), p95: percentile(max, 0.95), max: max.at(-1) ?? null },
  };
}

function setTimings(samples: ShareStateTimingSample[] | null) {
  currentTimingSamples = Array.isArray(samples) ? samples : [];
  currentTimingAgg = currentTimingSamples.length ? computeTimingAgg(currentTimingSamples) : null;
}

function seedTimingsFromLocalRun(stats: any) {
  const perf = stats?.perf?.tickExecutionMs;
  const sample: ShareStateTimingSample = {
    createdAt: new Date().toISOString(),
    analyzerCommit: analyzerCommit ?? null,
    bundleSha: lastBundleInfo?.sha ?? lastBundleInfo?.commit ?? null,
    runtimeMs: typeof stats?.runtimeMs === 'number' ? stats.runtimeMs : null,
    avgTickExecutionMs: typeof stats?.avgTickExecutionMs === 'number' ? stats.avgTickExecutionMs : null,
    tickP50Ms: typeof perf?.p50 === 'number' ? perf.p50 : null,
    tickP95Ms: typeof perf?.p95 === 'number' ? perf.p95 : null,
    tickP99Ms: typeof perf?.p99 === 'number' ? perf.p99 : null,
    tickMaxMs: typeof perf?.max === 'number' ? perf.max : null,
  };
  setTimings([sample]);
}

function renderCallsiteBarList(opts: {
  host: HTMLElement;
  title: string;
  items: Array<{ key: string; totalK: number; calls?: number }>;
  valueSuffix?: string;
}) {
  const { host, title, items, valueSuffix } = opts;
  host.innerHTML = '';

  const card = document.createElement('div');
  card.className = 'chart-card';
  card.innerHTML = '<h4>' + title + '</h4>';
  const body = document.createElement('div');
  body.style.display = 'flex';
  body.style.flexDirection = 'column';
  body.style.gap = '8px';
  body.style.padding = '8px 2px 2px 2px';

  const cleanLabel = (k: string) => {
    const s = String(k || '');
    if (!s) return '(unknown)';
    return s.length > 80 ? s.slice(0, 77) + '…' : s;
  };

  const max = items.reduce((m, it) => (typeof it?.totalK === 'number' && Number.isFinite(it.totalK) ? Math.max(m, it.totalK) : m), 0);
  const denom = max > 0 ? max : 1;

  const top = items
    .filter((it) => typeof it?.totalK === 'number' && Number.isFinite(it.totalK) && it.totalK > 0)
    .slice(0, 12);

  if (!top.length) {
    const empty = document.createElement('div');
    empty.style.color = 'var(--fg-3)';
    empty.style.fontSize = '12px';
    empty.textContent = 'No data';
    body.appendChild(empty);
  } else {
    for (const it of top) {
      const row = document.createElement('div');
      row.style.display = 'grid';
      row.style.gridTemplateColumns = '1fr 220px';
      row.style.gap = '10px';
      row.style.alignItems = 'center';

      const left = document.createElement('div');
      left.style.minWidth = '0';
      const lbl = document.createElement('div');
      lbl.textContent = cleanLabel(it.key);
      lbl.title = String(it.key || '');
      lbl.style.fontSize = '12px';
      lbl.style.color = 'var(--fg)';
      lbl.style.whiteSpace = 'nowrap';
      lbl.style.overflow = 'hidden';
      lbl.style.textOverflow = 'ellipsis';

      const meta = document.createElement('div');
      meta.style.fontSize = '11px';
      meta.style.color = 'var(--fg-3)';
      const calls = typeof it.calls === 'number' && Number.isFinite(it.calls) ? it.calls : 0;
      meta.textContent = calls ? `${calls} calls` : '';

      left.appendChild(lbl);
      if (meta.textContent) left.appendChild(meta);

      const right = document.createElement('div');
      right.style.display = 'grid';
      right.style.gridTemplateColumns = '1fr auto';
      right.style.gap = '10px';
      right.style.alignItems = 'center';

      const barWrap = document.createElement('div');
      barWrap.style.height = '10px';
      barWrap.style.background = 'var(--track)';
      barWrap.style.borderRadius = '999px';
      barWrap.style.overflow = 'hidden';

      const bar = document.createElement('div');
      bar.style.height = '100%';
      bar.style.width = `${Math.max(0, Math.min(100, (it.totalK / denom) * 100))}%`;
      bar.style.background = 'linear-gradient(90deg, #667eea, #764ba2)';
      barWrap.appendChild(bar);

      const val = document.createElement('div');
      val.style.fontSize = '12px';
      val.style.color = 'var(--fg-2)';
      val.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
      val.textContent = `${Math.round(it.totalK * 10) / 10}${valueSuffix ?? ''}`;

      right.appendChild(barWrap);
      right.appendChild(val);

      row.appendChild(left);
      row.appendChild(right);
      body.appendChild(row);
    }
  }

  card.appendChild(body);
  host.appendChild(card);
}

function renderRecipientsForKeys(opts: {
  host: HTMLElement;
  title: string;
  keys: string[];
  byClientId: Record<string, { totalK: number; byKeyK: Record<string, number> }>;
  replay: ReplayJson | null;
}) {
  const { host, title, keys, byClientId, replay } = opts;
  host.innerHTML = '';

  const card = document.createElement('div');
  card.className = 'chart-card';
  card.innerHTML = '<h4>' + title + '</h4>';

  const body = document.createElement('div');
  body.style.display = 'flex';
  body.style.flexDirection = 'column';
  body.style.gap = '10px';
  body.style.padding = '8px 2px 2px 2px';

  const presentKeys = keys.filter((k) => k && typeof k === 'string');
  if (!presentKeys.length) {
    const empty = document.createElement('div');
    empty.style.color = 'var(--fg-3)';
    empty.style.fontSize = '12px';
    empty.textContent = 'No matching callsites in top list';
    body.appendChild(empty);
  } else {
    for (const key of presentKeys) {
      const rows: Array<{ cid: string; k: number }> = [];
      for (const [cid, row] of Object.entries(byClientId || {})) {
        const v = row?.byKeyK?.[key];
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) rows.push({ cid, k: v });
      }
      rows.sort((a, b) => b.k - a.k);

      const line = document.createElement('div');
      line.style.display = 'grid';
      line.style.gridTemplateColumns = '1fr';
      line.style.gap = '6px';

      const kEl = document.createElement('div');
      kEl.style.fontSize = '12px';
      kEl.style.color = 'var(--fg)';
      kEl.style.fontFamily =
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
      kEl.textContent = key;

      const top = rows.slice(0, 6);
      const detail = document.createElement('div');
      detail.style.fontSize = '12px';
      detail.style.color = 'var(--fg-2)';
      detail.textContent = top.length
        ? top
            .map((r) => `${getPlayerName(r.cid, replay)} ${Math.round(r.k * 10) / 10}K`)
            .join(' | ')
        : '(no recipients)';

      line.appendChild(kEl);
      line.appendChild(detail);
      body.appendChild(line);
    }
  }

  card.appendChild(body);
  host.appendChild(card);
}

function displayQuickAnalysis(replay: ReplayJson) {
  ($('results') as HTMLElement).style.display = 'block';
  ($('computedWrap') as HTMLElement).style.display = 'none';
  $('computedGrid').innerHTML = '';

  try {
    const resultsEl = $('results');
    const h2 = resultsEl.querySelector('h2');
    if (h2) {
      let controls = document.getElementById('quickCardsControls') as HTMLDivElement | null;
      if (!controls) {
        controls = document.createElement('div');
        controls.id = 'quickCardsControls';
        controls.className = 'ofra-cards-controls';
        h2.insertAdjacentElement('afterend', controls);
      }

      let resetBtn = document.getElementById('quickCardsReset') as HTMLButtonElement | null;
      if (!resetBtn) {
        resetBtn = document.createElement('button');
        resetBtn.id = 'quickCardsReset';
      }
      resetBtn.type = 'button';
      resetBtn.className = 'ofra-reset-btn';
      resetBtn.textContent = 'Reset cards';
      resetBtn.onclick = () => {
        closeAvailablePopover();
        try {
          localStorage.removeItem(cardLayoutStorageKey('quick'));
        } catch {
          // ignore
        }
        if (currentReplay) displayQuickAnalysis(currentReplay);
        if (lastComputedStats) displayComputed(lastComputedStats);
      };
      if (resetBtn.parentElement !== controls) controls.appendChild(resetBtn);

      let availBtn = document.getElementById('quickCardsAvailable') as HTMLButtonElement | null;
      if (!availBtn) {
        availBtn = document.createElement('button');
        availBtn.id = 'quickCardsAvailable';
        availBtn.type = 'button';
        availBtn.className = 'ofra-reset-btn';
        availBtn.textContent = '0 available';
      }
      availBtn.onclick = () => openAvailablePopover('quick', availBtn as HTMLButtonElement);
      if (availBtn.parentElement !== controls) controls.appendChild(availBtn);

      updateAvailableButton('quick');
    }
  } catch {
    // ignore
  }

  const statsGrid = $('statsGrid');
  statsGrid.innerHTML = '';
  initCardGridInteractions('quick', statsGrid);

  const cfg = replay?.info?.config ?? {};
  const players = Array.isArray(replay?.info?.players) ? replay.info.players : [];
  const turns = Array.isArray(replay?.turns) ? replay.turns : [];
  const totalTurns =
    typeof (replay as any)?.info?.num_turns === 'number'
      ? Number((replay as any).info.num_turns)
      : typeof (replay as any)?.info?.numTurns === 'number'
        ? Number((replay as any).info.numTurns)
        : turns.length;

  // Find winner if available
  let winnerName = 'Unknown';
  let winnerSub: string | undefined = undefined;
  const winner = replay?.info?.winner;
  if (Array.isArray(winner) && winner.length >= 2) {
    console.log('winner', winner);
    const formatted = formatWinnerTuple(winner, currentReplay, { missingLabel: 'Unknown', hideClientIds: false });
    winnerName = formatted.label;
    winnerSub = formatted.sub;
  }

  // Count human players (players without killedAt)
  const humanPlayers = players.filter((p: any) => !p.stats?.killedAt).length;

  // Format duration
  const duration = replay?.info?.duration;
  const durationStr = typeof duration === 'number' ? formatDuration(duration) : '';

  const cards: Array<[string, string | number, string, string?]> = [
    ['🏆', winnerName, 'Winner', winnerSub],
    ['⏱️', durationStr || 'Unknown', 'Duration'],
    ['🗺️', cfg?.gameMap ?? '', 'Map'],
    ['🎯', cfg?.gameMode ?? '', 'Mode'],
    ['👥', humanPlayers, 'Active Players'],
    ['🤖', cfg?.bots ?? 0, 'Bots'],
    ['📼', turns.length, 'Turns Recorded'],
    ['🔢', cfg?.maxPlayers ?? '', 'Max Players'],
  ];

  for (const [icon, value, label, sub] of cards) {
    let displayValue = value;
    let displayLabel = label;
    if (label === 'Turns Recorded') {
      displayValue = totalTurns;
      displayLabel = 'Turns (total)';
    }
    const card = statCard(icon, displayValue, displayLabel, sub);
    const id = label === 'Turns Recorded' || displayLabel === 'Turns (total)' ? 'turns' : slugifyCardId(label);
    decorateCardEl('quick', card, id, displayLabel);
    statsGrid.appendChild(card);
  }

  applyCardLayoutToGrid('quick', statsGrid);
  updateAvailableButton('quick');
  refreshAvailablePopover();
}

function getPlayerName(clientID: string, replay: ReplayJson | null): string {
  if (!replay) return clientID;
  const players = Array.isArray(replay?.info?.players) ? replay.info.players : [];
  const player = players.find((p: any) => p.clientID === clientID);
  return player?.username || clientID;
}

function formatWinnerTuple(
  winner: unknown,
  replay: ReplayJson | null,
  opts?: { missingLabel?: string; hideClientIds?: boolean },
): { label: string; sub?: string } {
  const missingLabel = opts?.missingLabel ?? 'Unknown';
  const hideClientIds = !!opts?.hideClientIds;
  const players = Array.isArray(replay?.info?.players) ? replay.info.players : [];

  if (!Array.isArray(winner) || winner.length < 2) return { label: missingLabel };

  const winnerType = winner[0];
  const winnerValue = winner[1];

  const displayNameForClientId = (clientId: string) => {
    if (!clientId) return '';
    const player = players.find((p: any) => p?.clientID === clientId);
    const name = player?.username && String(player.username).trim() ? String(player.username) : '';
    if (name) return name;
    return hideClientIds ? '' : clientId;
  };

  if (winnerType === 'player') {
    const winnerClientId = String(winnerValue ?? '');
    const label = hideClientIds ? displayNameForClientId(winnerClientId) : getPlayerName(winnerClientId, replay);
    return { label: label || missingLabel };
  }

  if (winnerType === 'team') {
    const teamName = String(winnerValue ?? 'Unknown');
    const winnerClientIds = (winner as any[])
      .slice(2)
      .map((id: any) => String(id ?? ''))
      .filter((id: string) => id.length > 0);
    const names = winnerClientIds.map(displayNameForClientId).filter(Boolean).join(', ');
    return { label: 'Team ' + teamName, sub: names || undefined };
  }

  return { label: hideClientIds ? missingLabel : String(winnerType) + ': ' + String(winnerValue) };
}

function ensureD3(): any {
  const d3 = (window as any).d3;
  if (!d3) throw new Error('D3 not loaded. Expected window.d3');
  return d3;
}

function getChartTooltipEl(): HTMLElement {
  const existing = document.getElementById('chartTooltip');
  if (existing) return existing;
  const el = document.createElement('div');
  el.id = 'chartTooltip';
  el.className = 'chart-tooltip';
  document.body.appendChild(el);
  return el;
}

function showChartTooltip(x: number, y: number, html: string) {
  const t = getChartTooltipEl();
  // Guard against undefined/NaN coordinates (can happen across some event types)
  const lx = Number.isFinite(x) ? x : 0;
  const ly = Number.isFinite(y) ? y : 0;
  t.style.left = lx + 'px';
  t.style.top = ly + 'px';
  t.innerHTML = html;
  t.style.display = 'block';
}

function hideChartTooltip() {
  const t = getChartTooltipEl();
  t.style.display = 'none';
}

function colorHashString(input: string) {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h = (h << 5) - h + c;
    h |= 0;
  }
  return Math.abs(h);
}

function clampNumber(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

function hslToHex(h: number, s: number, l: number) {
  const hue = ((h % 360) + 360) % 360;
  const sat = clampNumber(s, 0, 100) / 100;
  const light = clampNumber(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const hh = hue / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh >= 0 && hh < 1) {
    r = c;
    g = x;
  } else if (hh >= 1 && hh < 2) {
    r = x;
    g = c;
  } else if (hh >= 2 && hh < 3) {
    g = c;
    b = x;
  } else if (hh >= 3 && hh < 4) {
    g = x;
    b = c;
  } else if (hh >= 4 && hh < 5) {
    r = x;
    b = c;
  } else if (hh >= 5 && hh < 6) {
    r = c;
    b = x;
  }
  const m = light - c / 2;
  const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

function colorForIndex(index: number) {
  const hue = (index * 137.508) % 360;
  const satLevels = [72, 62, 82, 52];
  const lightLevels = [48, 60, 40, 68];
  const variant = index % (satLevels.length * lightLevels.length);
  const sat = satLevels[variant % satLevels.length];
  const light = lightLevels[Math.floor(variant / satLevels.length)];
  return hslToHex(hue, sat, light);
}

function teamHueForKey(teamKey: string) {
  const key = String(teamKey || '').trim().toLowerCase();
  const named: Record<string, number> = {
    red: 0,
    blue: 210,
    teal: 180,
    purple: 275,
    yellow: 55,
    orange: 28,
    green: 120,
  };
  if (Object.prototype.hasOwnProperty.call(named, key)) return named[key];
  return colorHashString(key) % 360;
}

function parseTeamIndex(teamKey: string): number | null {
  const raw = String(teamKey || '').trim();
  if (!raw) return null;
  const norm = raw.toLowerCase();
  const m = norm.match(/^(?:team\s*)?(\d{1,3})$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  const idx = Math.floor(n) - 1;
  return idx >= 0 ? idx : null;
}

function baseHueForTeamKey(opts: { teamKey: string; teamIndex: number; teamCount: number }): number {
  const { teamKey, teamIndex, teamCount } = opts;

  // Named teams map to fixed hues for consistency.
  const namedHue = teamHueForKey(teamKey);
  const key = String(teamKey || '').trim().toLowerCase();
  const isNamed =
    key === 'red' || key === 'blue' || key === 'teal' || key === 'purple' || key === 'yellow' || key === 'orange' || key === 'green';
  if (isNamed) return namedHue;

  // "Team 1" (or "1") should map based on its numeric index, not hashing.
  const parsedIdx = parseTeamIndex(teamKey);
  const idx = parsedIdx ?? Math.max(0, Math.floor(teamIndex));

  // <=8: use a fixed palette; >8: spread hues using golden-angle mapping.
  if (teamCount > 8) {
    return (idx * 137.508) % 360;
  }

  const palette = [210, 0, 180, 270, 55, 28, 120, 300];
  return palette[idx % palette.length];
}

function teamShades(count: number, baseHue: number) {
  const satMin = 50;
  const satMax = 85;
  const lightMin = 35;
  const lightMax = 70;
  const shades: string[] = [];
  for (let i = 0; i < count; i++) {
    const zig = i % 2 === 0 ? i / 2 : count - 1 - (i - 1) / 2;
    const t = count <= 1 ? 0.5 : zig / (count - 1);
    const sat = satMax - t * (satMax - satMin);
    const light = lightMin + t * (lightMax - lightMin);
    shades.push(hslToHex(baseHue, sat, light));
  }
  return shades;
}

function buildSeriesColorMap(
  clientIds: string[],
  replay: ReplayJson | null,
  teamAssignments?: { byClientId: Record<string, string>; teamsInOrder?: string[] | null } | null,
) {
  void replay;
  const engineTeams = teamAssignments?.byClientId && typeof teamAssignments.byClientId === 'object' ? teamAssignments.byClientId : null;
  const engineTeamFor = (cid: string): string | null => {
    if (!engineTeams) return null;
    const raw = (engineTeams as any)[cid];
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    return t ? t : null;
  };

  const teamGroups = new Map<string, string[]>();
  const unteamed: string[] = [];
  for (const cid of clientIds) {
    const teamKey = engineTeamFor(cid);
    if (!teamKey) {
      unteamed.push(cid);
      continue;
    }
    if (!teamGroups.has(teamKey)) teamGroups.set(teamKey, []);
    teamGroups.get(teamKey)!.push(cid);
  }

  const colorByClientId = new Map<string, string>();

  // Determine team order (stable) for hue assignment.
  const rawTeamsInOrder = (teamAssignments as any)?.teamsInOrder;
  let teamOrder: string[] = Array.isArray(rawTeamsInOrder)
    ? (rawTeamsInOrder as any[]).map((t) => String(t ?? '')).filter((t) => t.trim().length > 0)
    : [];
  if (teamOrder.length) {
    teamOrder = teamOrder.filter((t) => teamGroups.has(t));
  }
  const presentTeams = Array.from(teamGroups.keys());
  const missingInOrder = presentTeams.filter((t) => !teamOrder.includes(t));
  missingInOrder.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  teamOrder = teamOrder.concat(missingInOrder);
  if (!teamOrder.length) {
    teamOrder = presentTeams.slice().sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  const teamCount = teamOrder.length;
  const teamIndex = new Map<string, number>();
  teamOrder.forEach((t, idx) => teamIndex.set(t, idx));

  for (const [teamKey, members] of teamGroups.entries()) {
    const idx = teamIndex.get(teamKey) ?? 0;
    const baseHue = baseHueForTeamKey({ teamKey, teamIndex: idx, teamCount });
    const shades = teamShades(members.length, baseHue);
    members.forEach((cid, idx) => colorByClientId.set(cid, shades[idx]));
  }

  unteamed.forEach((cid, idx) => {
    colorByClientId.set(cid, colorForIndex(idx));
  });

  return colorByClientId;
}

function sortClientIdsForCharts(
  clientIds: string[],
  replay: ReplayJson | null,
  teamAssignments?: { byClientId: Record<string, string>; teamsInOrder?: string[] | null } | null,
) {
  const sorted = clientIds.slice().sort();
  const cfg = replay?.info?.config as any;
  const mode = String(cfg?.gameMode ?? '').trim().toLowerCase();
  if (mode !== 'team') return sorted;

  const players = Array.isArray(replay?.info?.players) ? replay.info.players : [];
  const nameByClientId = new Map<string, string>();
  for (const p of players) {
    const cid = p?.clientID;
    if (typeof cid === 'string' && cid.length) {
      const name = typeof p?.username === 'string' && p.username.trim() ? p.username.trim() : '';
      if (name) nameByClientId.set(cid, name);
    }
  }

  const engineTeams = teamAssignments?.byClientId && typeof teamAssignments.byClientId === 'object' ? teamAssignments.byClientId : null;
  const engineTeamFor = (cid: string): string | null => {
    if (!engineTeams) return null;
    const raw = (engineTeams as any)[cid];
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    return t ? t : null;
  };

  const teamByClientId = new Map<string, string>();
  for (const cid of clientIds) {
    const teamKey = engineTeamFor(cid);
    if (teamKey) teamByClientId.set(cid, teamKey);
  }
  if (teamByClientId.size === 0) return sorted;

  let teamOrder: string[] = Array.isArray(teamAssignments?.teamsInOrder)
    ? (teamAssignments!.teamsInOrder as any[]).map((t) => String(t ?? '')).filter((t) => t.trim().length > 0)
    : [];

  const uniqueTeams = Array.from(new Set(teamByClientId.values()));
  const extraTeams = uniqueTeams.filter((t) => !teamOrder.includes(t));
  if (extraTeams.length) {
    extraTeams.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    teamOrder = teamOrder.concat(extraTeams);
  }
  if (!teamOrder.length) {
    teamOrder = uniqueTeams.slice().sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  const teamIndex = new Map<string, number>();
  teamOrder.forEach((team, idx) => teamIndex.set(team, idx));

  const nameFor = (cid: string) => nameByClientId.get(cid) ?? cid;

  return clientIds.slice().sort((a, b) => {
    const ta = teamByClientId.get(a);
    const tb = teamByClientId.get(b);
    const ai = ta ? teamIndex.get(ta) ?? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
    const bi = tb ? teamIndex.get(tb) ?? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    const na = nameFor(a);
    const nb = nameFor(b);
    const nameCmp = na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
    if (nameCmp !== 0) return nameCmp;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });
}

type ZoomableMultiLineChart = {
  updateDomain: (domain: [number, number]) => void;
  syncZoomToDomain: (domain: [number, number]) => void;
  setVariant?: (variant: ChartVariant) => void;
  setLegendVisible?: (visible: boolean) => void;
};

function createZoomableMultiLineChart(opts: {
  container: HTMLElement;
  title: string;
  ticks: number[];
  series: Array<{ label: string; values: number[]; color: string }>;
  yLabel?: string;
  curve?: 'monotone' | 'linear' | 'step';
  variant?: ChartVariant;
  onZoomDomain?: (domain: [number, number], chart: ZoomableMultiLineChart) => void;
}): ZoomableMultiLineChart {
  const d3 = ensureD3();

  const { container, title, ticks, series, yLabel, curve, onZoomDomain } = opts;
  container.innerHTML = '';

  let variant: ChartVariant = opts.variant ?? 'line';

  const n = Math.max(0, ticks.length);
  const maxIdx = Math.max(0, n - 1);

  const width = container.clientWidth || 520;
  const height = container.clientHeight || 300;
  const margin = { top: 10, right: 14, bottom: 28, left: 54 };
  const innerW = Math.max(10, width - margin.left - margin.right);
  const innerH = Math.max(10, height - margin.top - margin.bottom);

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .style('overflow', 'visible');

  // Title
  svg
    .append('text')
    .attr('x', margin.left)
    .attr('y', margin.top - 2)
    .style('font-size', '12px')
    .style('font-weight', '600')
    .style('fill', 'var(--chart-title)')
    .text(title);

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  // Use index-domain for sync with brush; show tick labels via formatter.
  const x0 = d3.scaleLinear().domain([0, maxIdx]).range([0, innerW]);
  const xView = x0.copy();

  const BLOCK_SIZE = 64;
  const seriesValues: number[][] = [];
  const seriesBlockMax: number[][] = [];
  const seriesPts: Array<{ label: string; color: string; pts: Array<{ i: number; v: number }> }> = [];

  for (const s of series) {
    const values = new Array<number>(n);
    const pts = new Array<{ i: number; v: number }>(n);
    for (let i = 0; i < n; i++) {
      const v = Number.isFinite(s.values?.[i]) ? Number(s.values[i]) : 0;
      values[i] = v;
      pts[i] = { i, v };
    }

    const numBlocks = Math.max(1, Math.ceil(n / BLOCK_SIZE));
    const blocks = new Array<number>(numBlocks).fill(0);
    for (let i = 0; i < n; i++) {
      const b = (i / BLOCK_SIZE) | 0;
      const v = values[i];
      if (v > blocks[b]) blocks[b] = v;
    }
    seriesValues.push(values);
    seriesBlockMax.push(blocks);
    seriesPts.push({ label: s.label, color: s.color, pts });
  }

  const totalValues = new Array<number>(n).fill(0);
  for (let s = 0; s < seriesValues.length; s++) {
    const values = seriesValues[s];
    for (let i = 0; i < n; i++) totalValues[i] += values[i] ?? 0;
  }
  const totalBlockMax = (() => {
    const numBlocks = Math.max(1, Math.ceil(n / BLOCK_SIZE));
    const blocks = new Array<number>(numBlocks).fill(0);
    for (let i = 0; i < n; i++) {
      const b = (i / BLOCK_SIZE) | 0;
      const v = totalValues[i] ?? 0;
      if (v > blocks[b]) blocks[b] = v;
    }
    return blocks;
  })();

  const computeBaseYMaxForVariant = (v: ChartVariant) => {
    if (v === 'stacked-area-100') return 1;
    if (v === 'stacked-area') {
      const m = d3.max(totalValues, (x: any) => (Number.isFinite(x) ? x : 0)) ?? 0;
      return m > 0 ? m : 1;
    }
    const m = d3.max(seriesValues, (arr: any) => d3.max(arr, (x: any) => (Number.isFinite(x) ? x : 0))) ?? 0;
    return m > 0 ? m : 1;
  };

  let y = d3.scaleLinear().domain([0, computeBaseYMaxForVariant(variant)]).nice().range([innerH, 0]);
  let baseYMax = y.domain()[1] || 1;
  let yView = y.copy();

  // Gridlines
  const gridG = g.append('g').attr('class', 'grid');
  const renderGrid = () => {
    gridG.call(
      d3
        .axisLeft(yView)
        .ticks(5)
        .tickSize(-innerW)
        .tickFormat(() => ''),
    );
    gridG.selectAll('line').style('stroke', 'var(--chart-grid)');
  };
  renderGrid();

  const tickLabel = (d: any) => {
    const i = Math.max(0, Math.min(maxIdx, Math.round(Number(d))));
    return String(ticks[i] ?? 0);
  };

  // Axes
  const xAxisG = g.append('g').attr('transform', `translate(0,${innerH})`);
  const xAxis = d3.axisBottom(xView).ticks(6).tickFormat(tickLabel);
  const styleXAxis = () => {
    xAxisG.selectAll('text').style('font-size', '10px').style('fill', 'var(--chart-axis-text)');
    xAxisG.selectAll('path,line').style('stroke', 'var(--chart-axis-line)');
  };
  xAxisG.call(xAxis);
  styleXAxis();

  const yAxisG = g.append('g');
  const styleYAxis = () => {
    yAxisG.selectAll('text').style('font-size', '10px').style('fill', 'var(--chart-axis-text)');
    yAxisG.selectAll('path,line').style('stroke', 'var(--chart-axis-line)');
  };
  const renderYAxis = () => {
    const axis =
      variant === 'stacked-area-100'
        ? d3
            .axisLeft(yView)
            .ticks(5)
            .tickFormat((d: any) => `${Math.round(Math.max(0, Math.min(1, Number(d))) * 100)}%`)
        : d3.axisLeft(yView).ticks(5);
    yAxisG.call(axis);
    styleYAxis();
  };
  renderYAxis();

  if (yLabel) {
    g.append('text')
      .attr('x', -margin.left + 6)
      .attr('y', -8)
      .style('font-size', '10px')
      .style('fill', 'var(--chart-ylabel)')
      .text(yLabel);
  }

  const curveFn =
    curve === 'linear'
      ? d3.curveLinear
      : curve === 'step'
        ? d3.curveStepAfter
        : d3.curveMonotoneX;

  const clipId = 'ofra-clip-' + Math.random().toString(16).slice(2);
  const defs = svg.append('defs');
  defs
    .append('clipPath')
    .attr('id', clipId)
    .attr('clipPathUnits', 'userSpaceOnUse')
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', innerW)
    .attr('height', innerH);
  const clipG = g.append('g').attr('clip-path', `url(#${clipId})`);
  // Important: clipG stays untransformed; plotG is transformed on zoom/pan.
  const plotG = clipG.append('g');

  const focus = clipG.append('g').style('display', 'none');
  focus.append('line').attr('y1', 0).attr('y2', innerH).style('stroke', 'var(--chart-focus-line)');
  let dots: any = null;
  const ensureDots = () => {
    if (dots) return dots;
    dots = focus
      .selectAll('circle')
      .data(seriesPts)
      .enter()
      .append('circle')
      .attr('r', 3.5)
      .attr('fill', (d: any) => d.color)
      .style('stroke', 'var(--chart-dot-stroke)')
      .attr('stroke-width', 2);
    return dots;
  };

  const renderPlot = () => {
    plotG.selectAll('*').remove();
    y = d3.scaleLinear().domain([0, computeBaseYMaxForVariant(variant)]).nice().range([innerH, 0]);
    baseYMax = y.domain()[1] || 1;
    yView = y.copy();
    renderGrid();
    renderYAxis();

    if (variant === 'line') {
      const line = d3
        .line()
        .x((p: any) => x0(p.i))
        .y((p: any) => y(p.v))
        .curve(curveFn);
      for (const s of seriesPts) {
        plotG
          .append('path')
          .datum(s.pts)
          .attr('fill', 'none')
          .attr('stroke', s.color)
          .attr('stroke-width', 2)
          .attr('vector-effect', 'non-scaling-stroke')
          .attr('opacity', 0.95)
          .attr('d', line);
      }
      ensureDots();
      dots.style('display', null);
    } else {
      if (dots) dots.style('display', 'none');
      const keys = seriesPts.map((_, i) => String(i));
      const stack = d3.stack().keys(keys);
      const data = new Array<any>(n);
      for (let i = 0; i < n; i++) {
        const row: any = { i };
        const denom = variant === 'stacked-area-100' ? (totalValues[i] ?? 0) : 1;
        for (let s = 0; s < seriesValues.length; s++) {
          const raw = seriesValues[s]?.[i] ?? 0;
          row[String(s)] = denom > 0 ? raw / denom : 0;
        }
        data[i] = row;
      }
      const layers = stack(data);
      const area = d3
        .area()
        .x((d: any) => x0(d.data.i))
        .y0((d: any) => y(d[0]))
        .y1((d: any) => y(d[1]))
        .curve(curveFn);

      for (const layer of layers) {
        const idx = Number(layer.key);
        const color = seriesPts[idx]?.color ?? '#60a5fa';
        plotG
          .append('path')
          .datum(layer)
          .attr('d', area)
          .attr('fill', color)
          .attr('opacity', 0.38)
          .attr('stroke', color)
          .attr('stroke-width', 1)
          .attr('vector-effect', 'non-scaling-stroke');
      }
    }
  };

  renderPlot();

  const overlay = g
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', innerW)
    .attr('height', innerH)
    .attr('fill', 'transparent');

  overlay
    .on('mouseenter', () => focus.style('display', null))
    .on('mouseleave', () => {
      focus.style('display', 'none');
      hideChartTooltip();
    })
    .on('mousemove', (event: any) => {
      const [mx] = d3.pointer(event, overlay.node());
      const idxRaw = xView.invert(mx);
      const idx = Math.max(0, Math.min(maxIdx, Math.round(idxRaw)));
      const t = ticks[idx] ?? 0;

      focus.select('line').attr('transform', 'translate(' + xView(idx) + ',0)');
      if (variant === 'line' && dots) {
        dots
          .attr('cx', xView(idx))
          .attr('cy', (d: any) => yView(Number.isFinite(d.pts?.[idx]?.v) ? d.pts[idx].v : 0));
      }

      const denom = variant === 'stacked-area-100' ? (totalValues[idx] ?? 0) : 1;

      const rows = seriesPts
        .map((d) => ({
          label: d.label,
          value: Number.isFinite(d.pts?.[idx]?.v) ? d.pts[idx].v : 0,
          color: d.color,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 12)
        .map(
          (r) =>
            '<div><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:' +
            r.color +
            ';margin-right:6px;"></span>' +
            r.label +
            ' <span class="mono">' +
            (variant === 'stacked-area-100'
              ? `${Math.round(((denom > 0 ? r.value / denom : 0) * 1000)) / 10}%`
              : Math.round(r.value * 1000) / 1000) +
            '</span></div>',
        )
        .join('');

      showChartTooltip(
        event.clientX,
        event.clientY,
        '<div>turn <span class="mono">' + String(t) + '</span></div><div style="margin-top:6px;">' + rows + '</div>',
      );
    });

  // Track which series are visible (for legend toggling)
  const visibleSeriesSet = new Set(series.map((_, i) => i));
  void visibleSeriesSet;

  const legend = g.append('g').attr('transform', 'translate(0,20)');
  const legendItems = series.slice(0, 10);
  legendItems.forEach((s, idx) => {
    const y0 = idx * 14;
    legend.append('rect').attr('x', 0).attr('y', y0 + 1).attr('width', 10).attr('height', 10).attr('fill', s.color);
    legend.append('text').attr('x', 14).attr('y', y0 + 10).style('fill', 'var(--chart-legend-text)').attr('font-size', 11).text(s.label);
  });
  legend.style('display', 'none');

  const setLegendVisible = (visible: boolean) => {
    legend.style('display', visible ? null : 'none');
  };

  const clampDomain = (domain: [number, number]): [number, number] => {
    const a = Number.isFinite(domain[0]) ? domain[0] : 0;
    const b = Number.isFinite(domain[1]) ? domain[1] : maxIdx;
    const lo = Math.max(0, Math.min(maxIdx, Math.min(a, b)));
    const hi = Math.max(0, Math.min(maxIdx, Math.max(a, b)));
    if (hi === lo) return [Math.max(0, lo - 0.5), Math.min(maxIdx, hi + 0.5)];
    return [lo, hi];
  };

  let currentTransform: any = d3.zoomIdentity;
  let currentYMax = baseYMax;
  let yRafId = 0;
  let pendingYDomain: [number, number] | null = null;

  const maxInSeriesRange = (values: number[], blocks: number[], lo: number, hi: number): number => {
    if (hi < lo) return 0;
    let m = 0;
    const b0 = (lo / BLOCK_SIZE) | 0;
    const b1 = (hi / BLOCK_SIZE) | 0;
    if (b0 === b1) {
      for (let i = lo; i <= hi; i++) {
        const v = values[i] ?? 0;
        if (v > m) m = v;
      }
      return m;
    }

    const firstBlockEnd = Math.min(hi, (b0 + 1) * BLOCK_SIZE - 1);
    for (let i = lo; i <= firstBlockEnd; i++) {
      const v = values[i] ?? 0;
      if (v > m) m = v;
    }

    for (let b = b0 + 1; b < b1; b++) {
      const v = blocks[b] ?? 0;
      if (v > m) m = v;
    }

    const lastBlockStart = b1 * BLOCK_SIZE;
    for (let i = lastBlockStart; i <= hi; i++) {
      const v = values[i] ?? 0;
      if (v > m) m = v;
    }
    return m;
  };

  const computeVisibleYMax = (domain: [number, number]) => {
    if (n <= 0) return 1;
    if (variant === 'stacked-area-100') return 1;
    const d = clampDomain(domain);
    const lo = Math.max(0, Math.min(maxIdx, Math.floor(d[0])));
    const hi = Math.max(0, Math.min(maxIdx, Math.ceil(d[1])));
    let m = 0;
    if (variant === 'stacked-area') {
      m = maxInSeriesRange(totalValues, totalBlockMax, lo, hi);
    } else {
      for (let s = 0; s < seriesValues.length; s++) {
        m = Math.max(m, maxInSeriesRange(seriesValues[s], seriesBlockMax[s], lo, hi));
      }
    }
    return m > 0 ? m : 1;
  };

  const applyPlotTransform = () => {
    const yK = baseYMax / Math.max(1e-9, currentYMax);
    const ty = innerH * (1 - yK);
    plotG.attr('transform', `translate(${currentTransform.x},${ty}) scale(${currentTransform.k},${yK})`);
  };

  const updateYViewForDomain = (domain: [number, number]) => {
    const visibleMax = computeVisibleYMax(domain);
    yView.domain([0, visibleMax]).nice();
    const nextMax = yView.domain()[1] || 1;
    if (Math.abs(nextMax - currentYMax) < 1e-9) return;
    currentYMax = nextMax;
    renderGrid();
    renderYAxis();
  };

  const requestYViewUpdate = (domain: [number, number]) => {
    pendingYDomain = domain;
    if (yRafId) return;
    yRafId = requestAnimationFrame(() => {
      yRafId = 0;
      const d = pendingYDomain;
      pendingYDomain = null;
      if (!d) return;
      updateYViewForDomain(d);
      applyPlotTransform();
    });
  };

  const domainToTransform = (domain: [number, number]) => {
    const [a, b] = clampDomain(domain);
    const pxA = x0(a);
    const pxB = x0(b);
    const denom = Math.max(1e-6, pxB - pxA);
    const k = innerW / denom;
    return d3.zoomIdentity.scale(k).translate(-pxA, 0);
  };

  const api: ZoomableMultiLineChart = {
    updateDomain: (domain: [number, number]) => {
      const d = clampDomain(domain);
      xView.domain(d);
      xAxisG.call(xAxis);
      styleXAxis();
      updateYViewForDomain(d);
      currentTransform = domainToTransform(d);
      applyPlotTransform();
    },
    syncZoomToDomain: (domain: [number, number]) => {
      if (!zoom) return;
      overlay.call(zoom.transform as any, domainToTransform(domain));
    },
    setVariant: (nextVariant: ChartVariant) => {
      if (nextVariant === variant) return;
      variant = nextVariant;
      renderPlot();
      // Re-apply current domain (also refreshes y scaling).
      const d = xView.domain() as [number, number];
      updateYViewForDomain(d);
      applyPlotTransform();
    },
    setLegendVisible,
  };

  // Zoom/pan
  const minWindow = Math.min(80, Math.max(10, maxIdx));
  const maxScale = maxIdx > 0 ? Math.max(1, maxIdx / minWindow) : 1;
  const zoom = d3
    .zoom()
    .scaleExtent([1, maxScale])
    .translateExtent([
      [0, 0],
      [innerW, innerH],
    ])
    .extent([
      [0, 0],
      [innerW, innerH],
    ])
    .on('zoom', (event: any) => {
      const nextX = event.transform.rescaleX(x0);
      xView.domain(nextX.domain());
      xAxisG.call(xAxis);
      styleXAxis();
      currentTransform = event.transform;
      requestYViewUpdate(nextX.domain() as [number, number]);
      applyPlotTransform();
      onZoomDomain?.(nextX.domain() as [number, number], api);
    });

  overlay.call(zoom as any);

  return api;
}

function displayCharts(statsSeries: any, replay: ReplayJson | null) {
  const chartsWrap = $('chartsWrap') as HTMLElement;
  const chartsGrid = $('chartsGrid');
  chartsGrid.innerHTML = '';
  initCardGridInteractions('charts', chartsGrid);
   
  if (!statsSeries || !Array.isArray(statsSeries.ticks) || statsSeries.ticks.length === 0) {
    chartsWrap.style.display = 'none';
    return;
  }
  
  chartsWrap.style.display = 'block';

  const fullTicks = statsSeries.ticks as number[];
  const fullGlobalSeries = (statsSeries.globalSeries || {}) as Record<string, number[]>;
  const fullSeriesByClientId = (statsSeries.seriesByClientId || {}) as Record<string, Record<string, number[]>>;

  const maxIdx = Math.max(0, fullTicks.length - 1);

  const makeChartCard = (title: string) => {
    const card = document.createElement('div');
    card.className = 'chart-card';
    card.innerHTML =
      '<div class="chart-card-header"><h4>' +
      title +
      '</h4><div class="chart-card-tools"></div></div><div class="chart-container"></div>';
    const container = card.querySelector('.chart-container') as HTMLElement;
    // ensure a stable height for D3 layout
    container.style.height = '300px';
    chartsGrid.appendChild(card);
    decorateCardEl('charts', card, 'chart.' + slugifyCardId(title), title);
    return container;
  };

  const charts: ZoomableMultiLineChart[] = [];
  let syncingZoom = false;
  let syncingBrush = false;
  let brushScaleX: any = null;
  let brushBehavior: any = null;
  let brushG: any = null;
  const visibleCharts = new Set<ZoomableMultiLineChart>();
  const dirtyCharts = new Set<ZoomableMultiLineChart>();
  const chartElByChart = new Map<ZoomableMultiLineChart, Element>();
  const observerRoot = null;
  const prevObserver = (chartsGrid as any).__ofraChartsObserver as IntersectionObserver | undefined;
  if (prevObserver) prevObserver.disconnect();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const chart = (e.target as any).__ofraChart as ZoomableMultiLineChart | undefined;
        if (!chart) continue;
        if (e.isIntersecting) {
          visibleCharts.add(chart);
          const d = currentDomain;
          if (d && dirtyCharts.has(chart)) {
            dirtyCharts.delete(chart);
            syncingZoom = true;
            try {
              chart.syncZoomToDomain(d);
            } finally {
              syncingZoom = false;
            }
          }
        } else {
          visibleCharts.delete(chart);
        }
      }
    },
    { root: observerRoot, threshold: 0.05 },
  );
  (chartsGrid as any).__ofraChartsObserver = observer;

  const clampDomain = (domain: [number, number]): [number, number] => {
    if (maxIdx <= 0) return [0, 0];
    const a = Number.isFinite(domain[0]) ? domain[0] : 0;
    const b = Number.isFinite(domain[1]) ? domain[1] : maxIdx;
    const lo = Math.max(0, Math.min(maxIdx, Math.min(a, b)));
    let hi = Math.max(0, Math.min(maxIdx, Math.max(a, b)));
    if (hi === lo) hi = Math.min(maxIdx, lo + 1);
    return [lo, hi];
  };

  let currentDomain: [number, number] | null = null;
  let pendingDomain: { domain: [number, number]; source: 'brush' | 'zoom' | 'reset' | 'init'; sourceChart: ZoomableMultiLineChart | null } | null = null;
  let rafId = 0;

  const updateLabel = (domain: [number, number]) => {
    const [a, b] = clampDomain(domain);
    const ai = Math.max(0, Math.min(maxIdx, Math.round(a)));
    const bi = Math.max(0, Math.min(maxIdx, Math.round(b)));
    const tA = fullTicks[ai] ?? fullTicks[0] ?? 0;
    const tB = fullTicks[bi] ?? fullTicks[maxIdx] ?? 0;
    labelEl?.replaceChildren(document.createTextNode(`${tA} → ${tB} (samples ${ai}..${bi})`));
  };

  const applyDomain = (domainIn: [number, number], source: 'brush' | 'zoom' | 'reset' | 'init', sourceChart: ZoomableMultiLineChart | null) => {
    const domain = clampDomain(domainIn);
    const changed = !currentDomain || Math.abs(currentDomain[0] - domain[0]) > 1e-6 || Math.abs(currentDomain[1] - domain[1]) > 1e-6;
    currentDomain = domain;
    if (!changed) return;

    updateLabel(domain);

    // Sync overview brush selection (minimap)
    if (brushG && brushBehavior && brushScaleX && source !== 'brush') {
      syncingBrush = true;
      try {
        brushG.call(brushBehavior.move as any, [brushScaleX(domain[0]), brushScaleX(domain[1])]);
      } finally {
        syncingBrush = false;
      }
    }

    // Sync zoom transforms across charts (skip the originating chart on direct zoom).
    syncingZoom = true;
    try {
      for (const c of charts) {
        if (source === 'zoom' && sourceChart && c === sourceChart) continue;
        const el = chartElByChart.get(c);
        const isVisible = el ? visibleCharts.has(c) : true;
        if (!isVisible) {
          dirtyCharts.add(c);
          continue;
        }
        c.syncZoomToDomain(domain);
      }
    } finally {
      syncingZoom = false;
    }
  };

  const requestDomain = (domain: [number, number], source: 'brush' | 'zoom' | 'reset' | 'init', sourceChart: ZoomableMultiLineChart | null) => {
    pendingDomain = { domain, source, sourceChart };
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      const p = pendingDomain;
      pendingDomain = null;
      if (!p) return;
      applyDomain(p.domain, p.source, p.sourceChart);
    });
  };

	  const renderMultiLineChart = (opts: {
	    container: HTMLElement;
	    title: string;
	    ticks: number[];
	    series: Array<{ label: string; values: number[]; color: string }>;
	    yLabel?: string;
	    curve?: 'monotone' | 'linear' | 'step';
	  }) => {
	    const chartId = slugifyCardId(opts.title);
	    const defaultVariant = defaultChartVariant(chartId);
	    const storedVariant = readStoredChartVariant(chartId);
	    const initialVariant = storedVariant ?? defaultVariant;
	    let legendVisible = readStoredChartLegend(chartId) ?? false;

	    const card = opts.container.closest('.chart-card') as HTMLElement | null;
	    const tools = card?.querySelector('.chart-card-tools') as HTMLElement | null;
	    const select = document.createElement('select');
	    select.className = 'chart-variant-select';
    select.title = 'Chart style';
    select.setAttribute('aria-label', 'Chart style');
    select.innerHTML =
      '<option value="line">Line</option>' +
      '<option value="stacked-area">Stacked area</option>' +
      '<option value="stacked-area-100">100% stacked area</option>';
	    select.value = initialVariant;
	    tools?.appendChild(select);

	    const legendBtn = document.createElement('button');
	    legendBtn.type = 'button';
	    legendBtn.className = 'chart-tool-btn';
	    legendBtn.textContent = '☰';
	    legendBtn.title = 'Toggle legend';
	    legendBtn.setAttribute('aria-label', 'Toggle legend');
	    legendBtn.setAttribute('aria-pressed', legendVisible ? 'true' : 'false');
	    if (legendVisible) legendBtn.classList.add('chart-tool-btn-active');
	    tools?.appendChild(legendBtn);

	    const chart = createZoomableMultiLineChart({
	      ...opts,
	      variant: initialVariant,
	      onZoomDomain: (domain, c) => {
	        if (syncingZoom) return;
	        requestDomain(domain, 'zoom', c);
	      },
	    });
	    chart.setLegendVisible?.(legendVisible);

	    select.onchange = () => {
	      const next = select.value as ChartVariant;
	      if (next !== 'line' && next !== 'stacked-area' && next !== 'stacked-area-100') return;
	      writeStoredChartVariant(chartId, next);
	      chart.setVariant?.(next);
	      if (currentDomain) chart.syncZoomToDomain(currentDomain);
	    };

	    legendBtn.onclick = () => {
	      legendVisible = !legendVisible;
	      writeStoredChartLegend(chartId, legendVisible);
	      legendBtn.setAttribute('aria-pressed', legendVisible ? 'true' : 'false');
	      legendBtn.classList.toggle('chart-tool-btn-active', legendVisible);
	      chart.setLegendVisible?.(legendVisible);
	    };

	    charts.push(chart);
	    const observeEl = (opts.container.closest('.chart-card') as Element | null) ?? opts.container;
	    (observeEl as any).__ofraChart = chart;
    chartElByChart.set(chart, observeEl);
    visibleCharts.add(chart);
    observer.observe(observeEl);
  };

  const renderAllCharts = (aIn: number, bIn: number) => {
    if (charts.length > 0) return;
    void aIn;
    void bIn;

    const ticks = fullTicks;
    const globalSeries: Record<string, number[]> = {};
    for (const [k, arr] of Object.entries(fullGlobalSeries)) {
      globalSeries[k] = Array.isArray(arr) ? arr : ticks.map(() => 0);
    }
    const seriesByClientId = {} as Record<string, Record<string, number[]>>;
    for (const cid of Object.keys(fullSeriesByClientId)) {
      const src = fullSeriesByClientId[cid];
      const dst: Record<string, number[]> = {};
      for (const [k, arr] of Object.entries(src)) {
        dst[k] = arr;
      }
      seriesByClientId[cid] = dst;
    }

    const teamsByClientId = (statsSeries as any)?.teamsByClientId;
    const teamsInOrderRaw = (statsSeries as any)?.teamsInOrder;
    const teamAssignments =
      teamsByClientId && typeof teamsByClientId === 'object'
        ? {
            byClientId: teamsByClientId as Record<string, string>,
            teamsInOrder: Array.isArray(teamsInOrderRaw)
              ? (teamsInOrderRaw as any[]).map((t) => String(t ?? '')).filter((t) => t.trim().length > 0)
              : null,
          }
        : null;

    const clientIds = sortClientIdsForCharts(Object.keys(seriesByClientId), replay, teamAssignments);
    const seriesColors = buildSeriesColorMap(clientIds, replay, teamAssignments);
    const pickSeries = (key: string) =>
      clientIds.map((cid, idx) => ({
        label: getPlayerName(cid, replay),
        values: Array.isArray(seriesByClientId[cid]?.[key]) ? seriesByClientId[cid][key] : ticks.map(() => 0),
        color: seriesColors.get(cid) ?? colorForIndex(idx),
      }));

    const pickGlobal = (key: string, label: string, color: string) => [
      {
        label,
        values: Array.isArray(globalSeries[key]) ? globalSeries[key] : ticks.map(() => 0),
        color,
      },
    ];

    const hasAnyNonZero = (key: string) => {
      for (const cid of clientIds) {
        const arr = seriesByClientId[cid]?.[key];
        if (!Array.isArray(arr)) continue;
        if (arr.some((v) => typeof v === 'number' && Number.isFinite(v) && v !== 0)) return true;
      }
      return false;
    };

    // Global charts (perf + replay-level)
    if (Array.isArray(globalSeries['perf.tickExecutionMs']) && globalSeries['perf.tickExecutionMs'].some((v) => v > 0)) {
      renderMultiLineChart({
        container: makeChartCard('Tick execution time (ms)'),
        title: 'Tick execution (ms)',
        ticks,
        series: pickGlobal('perf.tickExecutionMs', 'tick ms', '#60a5fa'),
        yLabel: 'ms',
      });
    }

    if (Array.isArray(globalSeries['replay.intents']) && globalSeries['replay.intents'].some((v) => v > 0)) {
      renderMultiLineChart({
        container: makeChartCard('Intents per tick'),
        title: 'Intents per tick',
        ticks,
        series: pickGlobal('replay.intents', 'intents', '#fbbf24'),
      });
    }

    if (
      Array.isArray(globalSeries['players.aliveHumans']) &&
      (globalSeries['players.aliveHumans'].some((v) => v > 0) ||
        (globalSeries['players.connectedAliveHumans'] || []).some((v) => v > 0) ||
        (globalSeries['players.spawnedHumans'] || []).some((v) => v > 0))
    ) {
      renderMultiLineChart({
        container: makeChartCard('Players alive (humans)'),
        title: 'Humans: alive / connected / spawned',
        ticks,
        series: [
          { label: 'alive', values: globalSeries['players.aliveHumans'] || ticks.map(() => 0), color: '#34d399' },
          { label: 'connected', values: globalSeries['players.connectedAliveHumans'] || ticks.map(() => 0), color: '#60a5fa' },
          { label: 'spawned', values: globalSeries['players.spawnedHumans'] || ticks.map(() => 0), color: '#a78bfa' },
        ],
        curve: 'step',
      });
    }

    // Curated set (now range-sliceable + hover tooltip)
    renderMultiLineChart({
      container: makeChartCard('🗺️ Territory owned (tiles)'),
      title: 'Tiles owned',
      ticks,
      series: pickSeries('state.tilesOwned'),
      curve: 'step',
    });

    renderMultiLineChart({
      container: makeChartCard('🪖 Troops (current)'),
      title: 'Troops',
      ticks,
      series: pickSeries('state.troops'),
    });

    renderMultiLineChart({
      container: makeChartCard('💰 Gold (current, K)'),
      title: 'Gold balance (K)',
      ticks,
      series: pickSeries('state.gold.k'),
      yLabel: 'K',
    });

    renderMultiLineChart({
      container: makeChartCard('🏴 Conquests (cumulative)'),
      title: 'Conquests',
      ticks,
      series: pickSeries('conquests'),
      curve: 'step',
    });

    renderMultiLineChart({
      container: makeChartCard('💰 Gold earned (K): work'),
      title: 'Gold work (K)',
      ticks,
      series: pickSeries('gold.work.k'),
      yLabel: 'K',
    });

    renderMultiLineChart({
      container: makeChartCard('⚔️ Gold earned (K): war'),
      title: 'Gold war (K)',
      ticks,
      series: pickSeries('gold.war.k'),
      yLabel: 'K',
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Gold earned (K): trade'),
      title: 'Gold trade (K)',
      ticks,
      series: pickSeries('gold.trade.k'),
      yLabel: 'K',
    });

    renderMultiLineChart({
      container: makeChartCard('Gold earned (K): train'),
      title: 'Gold train (K)',
      ticks,
      series: pickSeries('gold.train.k'),
      yLabel: 'K',
    });

    if (hasAnyNonZero('gold.donations.sent.k')) {
      renderMultiLineChart({
        container: makeChartCard('Gold donations (K): sent'),
        title: 'Gold donations sent (K)',
        ticks,
        series: pickSeries('gold.donations.sent.k'),
        yLabel: 'K',
      });
    }

    if (hasAnyNonZero('gold.donations.received.k')) {
      renderMultiLineChart({
        container: makeChartCard('Gold donations (K): received'),
        title: 'Gold donations received (K)',
        ticks,
        series: pickSeries('gold.donations.received.k'),
        yLabel: 'K',
      });
    }

    if (hasAnyNonZero('troops.donations.sent')) {
      renderMultiLineChart({
        container: makeChartCard('Troop donations: sent'),
        title: 'Troop donations sent',
        ticks,
        series: pickSeries('troops.donations.sent'),
      });
    }

    if (hasAnyNonZero('troops.donations.received')) {
      renderMultiLineChart({
        container: makeChartCard('Troop donations: received'),
        title: 'Troop donations received',
        ticks,
        series: pickSeries('troops.donations.received'),
      });
    }

    renderMultiLineChart({
      container: makeChartCard('⚔️ Attacks sent (cumulative troops)'),
      title: 'Attacks sent',
      ticks,
      series: pickSeries('attacks.sent'),
    });

    renderMultiLineChart({
      container: makeChartCard('🛡️ Attacks received (cumulative troops)'),
      title: 'Attacks received',
      ticks,
      series: pickSeries('attacks.recv'),
    });

    renderMultiLineChart({
      container: makeChartCard('↩️ Attacks cancelled (cumulative troops)'),
      title: 'Attacks cancelled',
      ticks,
      series: pickSeries('attacks.cancel'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: trade sent'),
      title: 'Boats trade sent',
      ticks,
      series: pickSeries('boats.trade.sent'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: trade arrived'),
      title: 'Boats trade arrived',
      ticks,
      series: pickSeries('boats.trade.arrive'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: trade captured'),
      title: 'Boats trade captured',
      ticks,
      series: pickSeries('boats.trade.capture'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: trade destroyed'),
      title: 'Boats trade destroyed',
      ticks,
      series: pickSeries('boats.trade.destroy'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: transport sent'),
      title: 'Boats transport sent',
      ticks,
      series: pickSeries('boats.trans.sent'),
    });

    renderMultiLineChart({
      container: makeChartCard('🚢 Boats: transport arrived'),
      title: 'Boats transport arrived (total)',
      ticks,
      series: pickSeries('boats.trans.arrive'),
    });

    renderMultiLineChart({
      container: makeChartCard('Boats: transport arrived (excluding returns)'),
      title: 'Boats transport arrived (excluding returns)',
      ticks,
      series: pickSeries('boats.trans.arrive.success'),
    });

    renderMultiLineChart({
      container: makeChartCard('Boats: transport returned/cancelled'),
      title: 'Boats transport returned/cancelled',
      ticks,
      series: pickSeries('boats.trans.arrive.return'),
    });

    renderMultiLineChart({
      container: makeChartCard('💣 Bombs: abomb launch'),
      title: 'Bombs abomb launch',
      ticks,
      series: pickSeries('bombs.abomb.launch'),
    });

    renderMultiLineChart({
      container: makeChartCard('💣 Bombs: abomb land'),
      title: 'Bombs abomb land',
      ticks,
      series: pickSeries('bombs.abomb.land'),
    });

    renderMultiLineChart({
      container: makeChartCard('💣 Bombs: abomb intercept'),
      title: 'Bombs abomb intercept',
      ticks,
      series: pickSeries('bombs.abomb.intercept'),
    });

    renderMultiLineChart({
      container: makeChartCard('💣 Bombs: hbomb launch'),
      title: 'Bombs hbomb launch',
      ticks,
      series: pickSeries('bombs.hbomb.launch'),
    });

    renderMultiLineChart({
      container: makeChartCard('💣 Bombs: mirv launch'),
      title: 'Bombs mirv launch',
      ticks,
      series: pickSeries('bombs.mirv.launch'),
    });

    renderMultiLineChart({
      container: makeChartCard('🏗️ Units: city built'),
      title: 'Units city built',
      ticks,
      series: pickSeries('units.city.built'),
    });

    renderMultiLineChart({
      container: makeChartCard('🏗️ Units: port built'),
      title: 'Units port built',
      ticks,
      series: pickSeries('units.port.built'),
    });

    renderMultiLineChart({
      container: makeChartCard('🏗️ Units: factory built'),
      title: 'Units factory built',
      ticks,
      series: pickSeries('units.fact.built'),
    });

    renderMultiLineChart({
      container: makeChartCard('🏗️ Units: warship built'),
      title: 'Units warship built',
      ticks,
      series: pickSeries('units.wshp.built'),
    });
  };

  // Range controls (time slice selection) - brush
  const controls = document.getElementById('chartControls') as HTMLElement | null;
  const labelEl = document.getElementById('chartRangeLabel') as HTMLElement | null;
  const brushHost = document.getElementById('chartBrush') as HTMLElement | null;
  const resetEl = document.getElementById('chartRangeReset') as HTMLButtonElement | null;

  if (controls && labelEl && brushHost && resetEl) {
    controls.style.display = 'flex';
    const d3 = ensureD3();
    const updateLabel = (a: number, b: number) => {
      const tA = fullTicks[a] ?? fullTicks[0] ?? 0;
      const tB = fullTicks[b] ?? fullTicks[maxIdx] ?? 0;
      labelEl.textContent = `${tA} → ${tB} (samples ${a}..${b})`;
    };

    const applyRange = (aIn: number, bIn: number) => {
      const a = Math.max(0, Math.min(maxIdx, Math.min(aIn, bIn)));
      const b = Math.max(0, Math.min(maxIdx, Math.max(aIn, bIn)));
      renderAllCharts(a, b);
      requestDomain([a, b], 'brush', null);
    };

    // Build brush timeline
    brushHost.innerHTML = '';
    const w = Math.max(280, brushHost.clientWidth || 520);
    const h = 54;
    const m = { left: 10, right: 10, top: 8, bottom: 18 };
    const innerW = w - m.left - m.right;
    const innerH = h - m.top - m.bottom;

    const svg = d3.select(brushHost).append('svg').attr('width', w).attr('height', h);
    const g = svg.append('g').attr('transform', `translate(${m.left},${m.top})`);

    const x = d3.scaleLinear().domain([0, maxIdx]).range([0, innerW]);
    brushScaleX = x;

    // Overview/minimap series behind the brush selection.
    const preferredKeys = ['replay.intents', 'perf.tickExecutionMs', 'players.aliveHumans'];
    const hasSignal = (arr: any) =>
      Array.isArray(arr) && arr.some((v: any) => typeof v === 'number' && Number.isFinite(v) && v !== 0);
    let overviewKey = preferredKeys.find((k) => hasSignal((fullGlobalSeries as any)[k]));
    if (!overviewKey) overviewKey = Object.keys(fullGlobalSeries).find((k) => hasSignal((fullGlobalSeries as any)[k]));
    const overview = overviewKey ? (fullGlobalSeries as any)[overviewKey] : null;
    if (overviewKey && overview && hasSignal(overview)) {
      const maxV = d3.max(overview, (v: any) => (Number.isFinite(v) ? v : 0)) ?? 0;
      if (maxV > 0) {
        const yMini = d3.scaleLinear().domain([0, maxV]).nice().range([innerH, 0]);
        const area = d3
          .area()
          .x((d: any, i: number) => x(i))
          .y0(innerH)
          .y1((d: any) => yMini(Number.isFinite(d) ? d : 0));
        g.append('path')
          .datum(overview)
          .attr('d', area)
          .attr('fill', 'var(--chart-grid)')
          .attr('opacity', 0.25);
      }
    }
    const axis = d3.axisBottom(x).ticks(6).tickFormat((d: any) => {
      const idx = Math.max(0, Math.min(maxIdx, Math.round(Number(d))));
      return String(fullTicks[idx] ?? 0);
    });

    g.append('g')
      .attr('class', 'brush-axis')
      .attr('transform', `translate(0,${innerH})`)
      .call(axis);

    brushBehavior = d3
      .brushX()
      .extent([
        [0, 0],
        [innerW, innerH],
      ])
      .on('brush end', (event: any) => {
        if (syncingBrush) return;
        if (!event.selection) return;
        const [x0, x1] = event.selection;
        requestDomain([x.invert(x0), x.invert(x1)], 'brush', null);
      });

    brushG = g.append('g').call(brushBehavior);

    // Default: full range selected
    syncingBrush = true;
    try {
      brushG.call(brushBehavior.move as any, [x(0), x(maxIdx)]);
    } finally {
      syncingBrush = false;
    }

    resetEl.onclick = () => {
      requestDomain([0, maxIdx], 'reset', null);
    };

    let resetCardsBtn = document.getElementById('chartCardsReset') as HTMLButtonElement | null;
    if (!resetCardsBtn) {
      resetCardsBtn = document.createElement('button');
      resetCardsBtn.id = 'chartCardsReset';
      resetCardsBtn.type = 'button';
      resetCardsBtn.className = 'ofra-reset-btn';
      resetCardsBtn.textContent = 'Reset cards';
      controls.appendChild(resetCardsBtn);
    }
    resetCardsBtn.onclick = () => {
      closeAvailablePopover();
      try {
          localStorage.removeItem(cardLayoutStorageKey('charts'));
      } catch {
        // ignore
      }
      applyCardLayoutToGrid('charts', chartsGrid);
      updateAvailableButton('charts');
      refreshAvailablePopover();
    };

    let availBtn = document.getElementById('chartCardsAvailable') as HTMLButtonElement | null;
    if (!availBtn) {
      availBtn = document.createElement('button');
      availBtn.id = 'chartCardsAvailable';
      availBtn.type = 'button';
      availBtn.className = 'ofra-reset-btn';
      availBtn.textContent = '0 available';
      controls.appendChild(availBtn);
    }
    availBtn.onclick = () => openAvailablePopover('charts', availBtn as HTMLButtonElement);
    updateAvailableButton('charts');

    // Initial render
    renderAllCharts(0, maxIdx);
    applyCardLayoutToGrid('charts', chartsGrid);
    updateAvailableButton('charts');
    refreshAvailablePopover();
    requestDomain([0, maxIdx], 'init', null);
    return;
  }

  // Fallback (no controls found): full range
  renderAllCharts(0, maxIdx);
  applyCardLayoutToGrid('charts', chartsGrid);
  updateAvailableButton('charts');
  refreshAvailablePopover();
  requestDomain([0, maxIdx], 'init', null);
}

function displayComputed(stats: any) {
  lastComputedStats = stats;
  const computedWrap = $('computedWrap') as HTMLElement;
  const computedGrid = $('computedGrid');
  computedGrid.innerHTML = '';
  computedWrap.style.display = 'block';
  computedWrap.querySelectorAll('.economy-callsites').forEach((el) => el.remove());
  computedWrap.querySelectorAll('.ofra-timings-details').forEach((el) => el.remove());
  initCardGridInteractions('computed', computedGrid);

  try {
    const h3 = computedWrap.querySelector('h3');
    if (h3) {
      let controls = document.getElementById('computedCardsControls') as HTMLDivElement | null;
      if (!controls) {
        controls = document.createElement('div');
        controls.id = 'computedCardsControls';
        controls.className = 'ofra-cards-controls';
        h3.insertAdjacentElement('afterend', controls);
      }

      let resetBtn = document.getElementById('computedCardsReset') as HTMLButtonElement | null;
      if (!resetBtn) {
        resetBtn = document.createElement('button');
        resetBtn.id = 'computedCardsReset';
      }
      resetBtn.type = 'button';
      resetBtn.className = 'ofra-reset-btn';
      resetBtn.textContent = 'Reset cards';
      resetBtn.onclick = () => {
        closeAvailablePopover();
        try {
          localStorage.removeItem(cardLayoutStorageKey('computed'));
        } catch {
          // ignore
        }
        if (lastComputedStats) displayComputed(lastComputedStats);
      };
      if (resetBtn.parentElement !== controls) controls.appendChild(resetBtn);

      let availBtn = document.getElementById('computedCardsAvailable') as HTMLButtonElement | null;
      if (!availBtn) {
        availBtn = document.createElement('button');
        availBtn.id = 'computedCardsAvailable';
        availBtn.type = 'button';
        availBtn.className = 'ofra-reset-btn';
        availBtn.textContent = '0 available';
      }
      availBtn.onclick = () => openAvailablePopover('computed', availBtn as HTMLButtonElement);
      if (availBtn.parentElement !== controls) controls.appendChild(availBtn);

      let regenBtn = document.getElementById('computedRegenerateBtn') as HTMLButtonElement | null;
      if (!regenBtn) {
        regenBtn = document.createElement('button');
        regenBtn.id = 'computedRegenerateBtn';
        regenBtn.type = 'button';
        regenBtn.className = 'ofra-reset-btn';
        regenBtn.textContent = 'Regenerate';
      }
      regenBtn.onclick = () => {
        regenBtn!.disabled = true;
        regenerateCurrentReplay()
          .catch((e) => showStatus('Regenerate failed: ' + String((e as any)?.message ?? e), 'error'))
          .finally(() => {
            regenBtn!.disabled = false;
          });
      };
      if (regenBtn.parentElement !== controls) controls.appendChild(regenBtn);

      updateAvailableButton('computed');
    }
  } catch {
    // ignore
  }

  const winnerTuple = stats?.winnerSim;
  const formattedWinner = formatWinnerTuple(winnerTuple, currentReplay, {
    missingLabel: 'Missing (no Win update)',
    hideClientIds: true,
  });
  let winnerLabel = formattedWinner.label;
  let winnerSub: string | undefined = formattedWinner.sub;

  const timingAgg = currentTimingAgg;
  const fmtSec = (ms: number | null) =>
    typeof ms === 'number' && Number.isFinite(ms) ? Math.round(ms / 10) / 100 + 's' : 'n/a';
  const fmtMs = (ms: number | null) =>
    typeof ms === 'number' && Number.isFinite(ms) ? Math.round(ms * 100) / 100 + 'ms' : 'n/a';

  const runtimeMs = timingAgg?.runtimeMs?.p50 ?? (typeof stats?.runtimeMs === 'number' ? stats.runtimeMs : null);
  const avgTickMs =
    timingAgg?.avgTickExecutionMs?.p50 ?? (typeof stats?.avgTickExecutionMs === 'number' ? stats.avgTickExecutionMs : null);
  const p95TickMs =
    timingAgg?.tickP95Ms?.p50 ?? (typeof stats?.perf?.tickExecutionMs?.p95 === 'number' ? stats.perf.tickExecutionMs.p95 : null);
  const maxTickMs =
    timingAgg?.tickMaxMs?.max ?? (typeof stats?.perf?.tickExecutionMs?.max === 'number' ? stats.perf.tickExecutionMs.max : null);

  const cfg = currentReplay?.info?.config as any;
  const mode = String(cfg?.gameMode ?? '')
    .trim()
    .toLowerCase();
  const isTeamGame = mode === 'team';

  const teamAssignmentsRaw = stats?.teamAssignments;
  const teamSource = typeof teamAssignmentsRaw?.source === 'string' ? String(teamAssignmentsRaw.source) : null;
  const teamByClientId: Record<string, string> = {};
  const rawTeamByClientId = teamAssignmentsRaw?.byClientId;
  if (rawTeamByClientId && typeof rawTeamByClientId === 'object') {
    for (const [cidRaw, teamRaw] of Object.entries(rawTeamByClientId as Record<string, unknown>)) {
      if (typeof cidRaw !== 'string') continue;
      if (typeof teamRaw !== 'string') continue;
      const cid = cidRaw.trim();
      const team = teamRaw.trim();
      if (!cid || !team) continue;
      teamByClientId[cid] = team;
    }
  }
  const teamsInOrder = Array.isArray(teamAssignmentsRaw?.teamsInOrder)
    ? (teamAssignmentsRaw.teamsInOrder as any[]).map((t) => String(t ?? '')).filter((t) => t.trim().length > 0)
    : null;
  const teamAssignments = Object.keys(teamByClientId).length ? { byClientId: teamByClientId, teamsInOrder } : null;

  const teamCounts = new Map<string, number>();
  const isBotTeam = (t: string) => t.trim().toLowerCase() === 'bot';
  if (teamAssignments) {
    for (const t of Object.values(teamByClientId)) {
      if (isBotTeam(t)) continue;
      teamCounts.set(t, (teamCounts.get(t) ?? 0) + 1);
    }
  }
  const teamCount = teamCounts.size ? teamCounts.size : null;
  let teamSizeMax: number | null = null;
  for (const n of teamCounts.values()) {
    if (teamSizeMax === null || n > teamSizeMax) teamSizeMax = n;
  }

  const runtimeSub =
    timingAgg && timingAgg.samples
      ? `n=${timingAgg.samples} min=${fmtSec(timingAgg.runtimeMs.min)} p95=${fmtSec(timingAgg.runtimeMs.p95)} max=${fmtSec(timingAgg.runtimeMs.max)}`
      : undefined;
  const avgTickSub =
    timingAgg && timingAgg.samples
      ? `n=${timingAgg.samples} p95=${fmtMs(timingAgg.avgTickExecutionMs.p95)} max=${fmtMs(timingAgg.avgTickExecutionMs.max)}`
      : undefined;
  const p95Sub =
    timingAgg && timingAgg.samples
      ? `n=${timingAgg.samples} min=${fmtMs(timingAgg.tickP95Ms.min)} p95=${fmtMs(timingAgg.tickP95Ms.p95)} max=${fmtMs(timingAgg.tickP95Ms.max)}`
      : undefined;
  const maxSub =
    timingAgg && timingAgg.samples
      ? `n=${timingAgg.samples} median=${fmtMs(timingAgg.tickMaxMs.p50)} p95=${fmtMs(timingAgg.tickMaxMs.p95)}`
      : undefined;

  const cards: Array<[string, string | number, string, string?]> = [
    ['🏆', winnerLabel, 'Winner (sim)', winnerSub],
    //['🔄', stats?.turnsSimulated ?? 0, 'Turns Simulated'],
    //['🧮', stats?.lastTick ?? 0, 'Engine Ticks'],
    ['⏱️', fmtSec(runtimeMs), 'Runtime', runtimeSub],
    ['⚡', fmtMs(avgTickMs), 'Avg Tick Exec', avgTickSub],
    ['👥', stats?.playersCount ?? 0, 'Players (stats)'],
    ['🏴', stats?.totalConquests ?? '0', 'Conquests (total)'],
    ['🥇', stats?.topConquests ? getPlayerName(stats.topConquests.clientID, currentReplay)  : 'n/a',
       'Top Conquests',
        stats?.topConquests ? String(stats.topConquests.conquests) : 'n/a'
    ],
    //['⚙️', stats?.engineLoaded ? 'yes' : 'no', 'Engine loaded'],
  ];

  for (const [icon, value, label, sub] of cards) {
    const card = statCard(icon, value, label, sub);
    decorateCardEl('computed', card, 'computed.' + slugifyCardId(label), label);
    computedGrid.appendChild(card);
  }

  // Team count: engine-derived team assignment summary.
  try {
    if (isTeamGame) {
      const card = statCard('🤝', teamCount ?? 'missing', 'Team count', `max size ${teamSizeMax ?? 'n/a'}` + (teamSource ? ` | ${teamSource}` : ''));
      decorateCardEl('computed', card, 'computed.team-count', 'Team count');
      computedGrid.appendChild(card);
    }
  } catch {
    // ignore
  }

  // Team colors: show engine-derived team assignment -> chart color mapping (debug helper).
  try {
    if (isTeamGame && teamAssignments) {
      const seriesByClientId = stats?.statsSeries?.seriesByClientId;
      const clientIdsFromSeries =
        seriesByClientId && typeof seriesByClientId === 'object' ? Object.keys(seriesByClientId as Record<string, unknown>) : [];
      const clientIds = clientIdsFromSeries.length ? clientIdsFromSeries : Object.keys(teamByClientId);
      const sortedClientIds = sortClientIdsForCharts(clientIds, currentReplay, teamAssignments);
      const colorMap = buildSeriesColorMap(sortedClientIds, currentReplay, teamAssignments);

      const membersByTeam = new Map<string, string[]>();
      const unassigned: string[] = [];
      for (const cid of sortedClientIds) {
        const team = teamByClientId[cid];
        if (!team) {
          unassigned.push(cid);
          continue;
        }
        if (isBotTeam(team)) continue;
        if (!membersByTeam.has(team)) membersByTeam.set(team, []);
        membersByTeam.get(team)!.push(cid);
      }

      const orderedTeams = (() => {
        const seen = new Set<string>();
        const out: string[] = [];
        if (Array.isArray(teamsInOrder)) {
          for (const t of teamsInOrder) {
            const key = String(t ?? '').trim();
            if (!key || seen.has(key)) continue;
            if (!membersByTeam.has(key)) continue;
            seen.add(key);
            out.push(key);
          }
        }
        const rest = Array.from(membersByTeam.keys()).filter((t) => !seen.has(t));
        rest.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        out.push(...rest);
        return out;
      })();

      const card = document.createElement('div');
      card.className = 'chart-card';
      card.style.gridColumn = '1 / -1';
      card.innerHTML = '<h4>Team colors</h4>';

      const body = document.createElement('div');
      body.style.display = 'flex';
      body.style.flexDirection = 'column';
      body.style.gap = '8px';
      body.style.padding = '8px 2px 2px 2px';

      const summary = document.createElement('div');
      summary.style.fontSize = '12px';
      summary.style.color = 'var(--fg-2)';
      summary.textContent =
        `teams=${teamCount ?? 'n/a'}` +
        ` maxSize=${teamSizeMax ?? 'n/a'}` +
        ` assigned=${sortedClientIds.length - unassigned.length}/${sortedClientIds.length}` +
        (teamSource ? ` source=${teamSource}` : '');
      body.appendChild(summary);

      const list = document.createElement('div');
      list.style.display = 'flex';
      list.style.flexDirection = 'column';
      list.style.gap = '6px';
      list.style.maxHeight = '360px';
      list.style.overflow = 'auto';
      list.style.paddingRight = '6px';

      const monoFont =
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

      for (const teamKey of orderedTeams) {
        const members = membersByTeam.get(teamKey) ?? [];
        if (!members.length) continue;

        const row = document.createElement('div');
        row.style.display = 'grid';
        row.style.gridTemplateColumns = '120px 1fr';
        row.style.gap = '10px';
        row.style.alignItems = 'center';

        const left = document.createElement('div');
        left.style.fontSize = '12px';
        left.style.color = 'var(--fg)';
        left.style.fontFamily = monoFont;
        left.textContent = `${teamKey} (${members.length})`;
        row.appendChild(left);

        const right = document.createElement('div');
        right.style.display = 'flex';
        right.style.flexWrap = 'wrap';
        right.style.gap = '8px';

        for (const cid of members) {
          const pill = document.createElement('div');
          pill.style.display = 'inline-flex';
          pill.style.alignItems = 'center';
          pill.style.gap = '6px';
          pill.style.padding = '2px 8px';
          pill.style.border = '1px solid var(--border)';
          pill.style.borderRadius = '999px';
          pill.style.background = 'var(--panel-bg-muted)';
          pill.title = cid;

          const swatch = document.createElement('span');
          swatch.style.display = 'inline-block';
          swatch.style.width = '10px';
          swatch.style.height = '10px';
          swatch.style.borderRadius = '3px';
          swatch.style.background = colorMap.get(cid) ?? '#60a5fa';
          pill.appendChild(swatch);

          const name = document.createElement('span');
          name.style.fontSize = '12px';
          name.style.color = 'var(--fg-2)';
          name.textContent = getPlayerName(cid, currentReplay);
          pill.appendChild(name);

          right.appendChild(pill);
        }

        row.appendChild(right);
        list.appendChild(row);
      }

      if (unassigned.length) {
        const row = document.createElement('div');
        row.style.fontSize = '12px';
        row.style.color = 'var(--fg-2)';
        row.textContent = `Unassigned: ${unassigned.map((cid) => getPlayerName(cid, currentReplay)).join(', ')}`;
        list.appendChild(row);
      }

      body.appendChild(list);
      card.appendChild(body);
      decorateCardEl('computed', card, 'computed.team-colors', 'Team colors');
      computedGrid.appendChild(card);
    }
  } catch {
    // ignore
  }

  const extraCards: Array<[string, string | number, string, string?]> = [];
  extraCards.push(['p95', fmtMs(p95TickMs), 'Tick Exec p95', p95Sub]);
  extraCards.push(['max', fmtMs(maxTickMs), 'Tick Exec max', maxSub]);

  extraCards.push(['int', stats?.intents?.total ?? 0, 'Intents (total)']);
  extraCards.push([
    'avg',
    typeof stats?.intents?.avgPerTick === 'number' ? Math.round(stats.intents.avgPerTick * 100) / 100 : 'n/a',
    'Intents / tick',
  ]);

  extraCards.push([
    'hash',
    stats?.hashChecks ? `${stats.hashChecks.compared ?? 0} / ${stats.hashChecks.expectedHashes ?? 0}` : 'n/a',
    'Hashes (computed/expected)',
    stats?.hashChecks ? `mismatches ${stats.hashChecks.mismatches ?? 0}` : undefined,
  ]);

  /*extraCards.push([
    'unk',
    stats?.unknownClientIds?.total ?? 0,
    'Unknown clientIDs',
    stats?.unknownClientIds ? `mark-only ${stats.unknownClientIds.markOnly ?? 0}` : undefined,
  ]);*/

  // Economy hooks (source-map assisted). This fills gaps in StatsImpl for some versions (notably v27 "train").
  try {
    const eco = stats?.economyHooks;
    if (eco?.enabled) {
      const tradeByClientId = eco?.categories?.goldEarnedK?.tradeByClientId || {};
      const trainByClientId = eco?.categories?.goldEarnedK?.trainByClientId || {};

      const sum = (obj: Record<string, number>) =>
        Object.values(obj).reduce((acc, v) => acc + (typeof v === 'number' && Number.isFinite(v) ? v : 0), 0);

      const top = (obj: Record<string, number>) => {
        let bestId: string | null = null;
        let bestVal = -Infinity;
        for (const [cid, v] of Object.entries(obj)) {
          const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
          if (n > bestVal) {
            bestVal = n;
            bestId = cid;
          }
        }
        return bestId && Number.isFinite(bestVal) && bestVal > 0 ? { cid: bestId, k: bestVal } : null;
      };

      const tradeTotalK = sum(tradeByClientId);
      const trainTotalK = sum(trainByClientId);
      const tradeTop = top(tradeByClientId);
      const trainTop = top(trainByClientId);
      const tradeNonZero = Object.values(tradeByClientId).filter((v) => typeof v === 'number' && Number.isFinite(v) && v > 0).length;
      const trainNonZero = Object.values(trainByClientId).filter((v) => typeof v === 'number' && Number.isFinite(v) && v > 0).length;

      /*extraCards.push([
        'map',
        eco?.sourceMap?.available ? 'yes' : 'no',
        'Economy hooks sourcemap',
        eco?.sourceMap?.url ? String(eco.sourceMap.url) : undefined,
      ]);*/
      extraCards.push([
        '🚢 🥇',
        tradeTop ? getPlayerName(tradeTop.cid, currentReplay) : 'n/a',
        "Trade King",
        tradeTop ? String(Math.round(tradeTop.k * 10) / 10) + 'K' : 'n/a'
      ]);
      extraCards.push([
        '🚢 💰 Total',
        Math.round(tradeTotalK * 10) / 10,
        'Gold trade ships total'
      ]);

      extraCards.push([
        '🚂🥇',
        trainTop ? getPlayerName(trainTop.cid, currentReplay) : 'n/a',
        'Train King',
        trainTop
          ? String(Math.round(trainTop.k * 10) / 10) + 'K'
          : 'n/a'
      ]);
      extraCards.push([
        '🚂💰',
        Math.round(trainTotalK * 10) / 10,
        'Gold train total'
      ]);
    }
  } catch {
    // ignore
  }

  for (const [icon, value, label, sub] of extraCards) {
    const card = statCard(icon, value, label, sub);
    decorateCardEl('computed', card, 'computed.' + slugifyCardId(label), label);
    computedGrid.appendChild(card);
  }

  // Timings: show all collected samples (from ShareState timing table when configured).
  try {
    if (currentTimingSamples.length) {
      const agg = currentTimingAgg ?? computeTimingAgg(currentTimingSamples);

      const card = document.createElement('div');
      card.className = 'chart-card';
      card.innerHTML = '<h4>Timings (all runs)</h4>';

      const body = document.createElement('div');
      body.style.display = 'flex';
      body.style.flexDirection = 'column';
      body.style.gap = '8px';
      body.style.padding = '8px 2px 2px 2px';

      const summary = document.createElement('div');
      summary.style.fontSize = '12px';
      summary.style.color = 'var(--fg-2)';
      summary.textContent =
        `samples=${agg.samples}` +
        (agg.firstCreatedAt ? ` first=${agg.firstCreatedAt}` : '') +
        (agg.lastCreatedAt ? ` last=${agg.lastCreatedAt}` : '');
      body.appendChild(summary);

      const metrics = document.createElement('div');
      metrics.style.fontSize = '12px';
      metrics.style.color = 'var(--fg-2)';
      metrics.textContent =
        `runtime: min ${fmtSec(agg.runtimeMs.min)} · med ${fmtSec(agg.runtimeMs.p50)} · p95 ${fmtSec(agg.runtimeMs.p95)} · max ${fmtSec(agg.runtimeMs.max)} | ` +
        `tick p95: min ${fmtMs(agg.tickP95Ms.min)} · med ${fmtMs(agg.tickP95Ms.p50)} · p95 ${fmtMs(agg.tickP95Ms.p95)} · max ${fmtMs(agg.tickP95Ms.max)}`;
      body.appendChild(metrics);

      const details = document.createElement('details');
      details.className = 'ofra-timings-details';
      details.open = false;
      const sum = document.createElement('summary');
      sum.textContent = 'Show all timing samples';
      sum.style.cursor = 'pointer';
      sum.style.margin = '6px 0';
      details.appendChild(sum);

      const tableWrap = document.createElement('div');
      tableWrap.style.maxHeight = '260px';
      tableWrap.style.overflow = 'auto';
      tableWrap.style.border = '1px solid var(--border)';
      tableWrap.style.borderRadius = '10px';
      tableWrap.style.padding = '8px';
      tableWrap.style.background = 'var(--panel-bg-muted)';

      const table = document.createElement('table');
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '12px';
      table.innerHTML =
        '<thead><tr>' +
        '<th style="text-align:left; padding:6px 4px;">at</th>' +
        '<th style="text-align:right; padding:6px 4px;">runtime</th>' +
        '<th style="text-align:right; padding:6px 4px;">avg tick</th>' +
        '<th style="text-align:right; padding:6px 4px;">tick p95</th>' +
        '<th style="text-align:right; padding:6px 4px;">tick max</th>' +
        '<th style="text-align:left; padding:6px 4px;">bundle</th>' +
        '</tr></thead><tbody></tbody>';

      const tbody = table.querySelector('tbody') as HTMLTableSectionElement;
      for (const s of currentTimingSamples) {
        const tr = document.createElement('tr');
        tr.innerHTML =
          `<td style="padding:6px 4px; border-top:1px solid var(--border); white-space:nowrap;">${String(s.createdAt)}</td>` +
          `<td style="padding:6px 4px; border-top:1px solid var(--border); text-align:right; white-space:nowrap;">${fmtSec(s.runtimeMs)}</td>` +
          `<td style="padding:6px 4px; border-top:1px solid var(--border); text-align:right; white-space:nowrap;">${fmtMs(s.avgTickExecutionMs)}</td>` +
          `<td style="padding:6px 4px; border-top:1px solid var(--border); text-align:right; white-space:nowrap;">${fmtMs(s.tickP95Ms)}</td>` +
          `<td style="padding:6px 4px; border-top:1px solid var(--border); text-align:right; white-space:nowrap;">${fmtMs(s.tickMaxMs)}</td>` +
          `<td style="padding:6px 4px; border-top:1px solid var(--border); white-space:nowrap;">${String(s.bundleSha || '').slice(0, 8)}</td>`;
        tbody.appendChild(tr);
      }

      tableWrap.appendChild(table);
      details.appendChild(tableWrap);
      body.appendChild(details);

      card.appendChild(body);
      decorateCardEl('computed', card, 'computed.timings', 'Timings (all runs)');
      computedGrid.appendChild(card);
    }
  } catch {
    // ignore
  }

  applyCardLayoutToGrid('computed', computedGrid);
  updateAvailableButton('computed');
  refreshAvailablePopover();

  // Gold callsites: top sources/drains by callsite (amount + call count).
  try {
    const eco = stats?.economyHooks;
    const adds = eco?.gold?.adds?.top;
    const removes = eco?.gold?.removes?.top;
    if (Array.isArray(adds) || Array.isArray(removes)) {
      const section = document.createElement('details');
      section.className = 'economy-callsites';
      section.style.marginTop = '18px';

      section.open = false;

      const summary = document.createElement('summary');
      summary.textContent = 'Gold sources/drains by callsite';
      summary.style.margin = '10px 0';
      summary.style.cursor = 'pointer';
      section.appendChild(summary);

      const grid = document.createElement('div');
      grid.className = 'charts-grid';
      section.appendChild(grid);

      const left = document.createElement('div');
      const right = document.createElement('div');
      grid.appendChild(left);
      grid.appendChild(right);

      renderCallsiteBarList({
        host: left,
        title: 'Gold sources (top callsites)',
        items: Array.isArray(adds) ? adds : [],
        valueSuffix: 'K',
      });
      renderCallsiteBarList({
        host: right,
        title: 'Gold drains (top callsites)',
        items: Array.isArray(removes) ? removes : [],
        valueSuffix: 'K',
      });

      // Debug helper: show who receives TrainStation addGold (stationOwner vs trainOwner),
      // based on per-client totals for the top callsites we already ship.
      try {
        const byClientId = eco?.gold?.adds?.byClientId;
        const topKeys: string[] = Array.isArray(eco?.gold?.adds?.topKeys) ? eco.gold.adds.topKeys : [];
        if (byClientId && topKeys.length) {
          const trainKeys = topKeys.filter(
            (k) =>
              typeof k === 'string' &&
              (k.includes('src/core/game/TrainStation.ts:31') ||
                k.includes('src/core/game/TrainStation.ts:33') ||
                k.includes('src/core/game/TrainStation.ts:48') ||
                k.includes('src/core/game/TrainStation.ts:51')),
          );
          if (trainKeys.length) {
            const trainWrap = document.createElement('div');
            trainWrap.style.marginTop = '12px';
            const trainGrid = document.createElement('div');
            trainGrid.className = 'charts-grid';
            trainWrap.appendChild(trainGrid);
            const tLeft = document.createElement('div');
            trainGrid.appendChild(tLeft);
            renderRecipientsForKeys({
              host: tLeft,
              title: 'TrainStation recipients (by callsite)',
              keys: trainKeys.slice(0, 4),
              byClientId,
              replay: currentReplay,
            });
            section.appendChild(trainWrap);
          }
        }
      } catch {
        // ignore
      }

      computedWrap.appendChild(section);
    }
  } catch {
    // ignore
  }

  // Display charts if time-series data is available
  if (stats?.statsSeries && stats.statsSeries.ticks) {
    displayCharts(stats.statsSeries, currentReplay);
  }
}

async function getBundleInfo(version: string): Promise<BundleInfo> {
  const r = await fetch('/api/bundles/' + encodeURIComponent(version));
  if (r.ok) {
    const j = await r.json();
    if (j && j.url) return j;
  }
  const list = await fetch('/api/bundles').then((x) => x.json());
  const first = list?.bundles?.[0];
  if (first?.url) return first;
  throw new Error('No bundles available');
}

type ShareStateLatestResponse =
  | {
      available: true;
      replayKey: string;
      contentHash: string;
      payloadUrl: string;
      timings: ShareStateTimingSample[];
      timingsAgg: ShareStateTimingAgg;
    }
  | { available: false };

type ShareStateSubmitResponse = {
  ok: true;
  storedNewResult: boolean;
  contentHash: string;
  payloadUrl: string;
  timings: ShareStateTimingSample[];
  timingsAgg: ShareStateTimingAgg;
};

async function fetchShareStateLatest(replayKey: string): Promise<ShareStateLatestResponse | null> {
  try {
    const res = await fetch('/api/results/' + encodeURIComponent(replayKey) + '/latest');
    if (res.status === 404) return null;
    if (res.status === 503) return null; // storage not configured
    const text = await res.text();
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${text}`);
    return JSON.parse(text);
  } catch (e) {
    console.warn('ShareState latest fetch failed:', e);
    return null;
  }
}

async function fetchShareStatePayload(payloadUrl: string): Promise<any | null> {
  try {
    const res = await fetch(payloadUrl, { method: 'GET' });
    if (res.status === 404) return null;
    if (res.status === 503) return null;
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    return await res.json();
  } catch (e) {
    console.warn('ShareState payload fetch failed:', e);
    return null;
  }
}

async function submitShareState(stats: any): Promise<ShareStateSubmitResponse | null> {
  if (!currentReplay) return null;
  if (!currentReplayId || !currentReplayKey) return null;

  const mismatches = stats?.hashChecks?.mismatches;
  if (typeof mismatches !== 'number' || !Number.isFinite(mismatches) || mismatches !== 0) {
    showStatus(`Not stored: hash check mismatches (${String(mismatches ?? 'unknown')})`, 'error');
    return null;
  }

  const payload = {
    schemaVersion: 2,
    generatedAt: new Date().toISOString(),
    replayMeta: { replayId: currentReplayId, replayGitCommit: String(currentReplay?.gitCommit ?? '') },
    bundleMeta: {
      sha: lastBundleInfo?.sha ?? lastBundleInfo?.commit ?? null,
      tag: lastBundleInfo?.tag ?? null,
    },
    analyzerCommit,
    data: stats,
  };

  try {
    const res = await fetch('/api/results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        replayKey: currentReplayKey,
        replayId: currentReplayId,
        replayGitCommit: String(currentReplay?.gitCommit ?? ''),
        analyzerCommit,
        bundleSha: lastBundleInfo?.sha ?? lastBundleInfo?.commit ?? null,
        bundleTag: lastBundleInfo?.tag ?? null,
        schemaVersion: 2,
        payload,
      }),
    });

    const text = await res.text();
    if (res.status === 503) return null; // storage not configured
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${text}`);
    return JSON.parse(text);
  } catch (e) {
    console.warn('ShareState submit failed:', e);
    return null;
  }
}

async function handleReplayLoaded(replay: ReplayJson) {
  if (!replay?.gitCommit) throw new Error('Invalid replay: missing gitCommit');
  currentReplay = replay;

  const replayId = extractReplayId(replay);
  currentReplayId = replayId;
  currentReplayKey = replayKeyForId(replayId);
  currentContentHash = null;
  setTimings(null);
  setJoinReplayIdHash(replayId);
  try {
    const input = document.getElementById('replayIdInput') as HTMLInputElement | null;
    if (input) input.value = replayId;
  } catch {
    // ignore
  }

  displayQuickAnalysis(replay);
  setMeta([
    { k: 'Replay ID', v: String(replayId) },
    { k: 'Replay gitCommit', v: String(replay?.gitCommit ?? '(unknown)') },
  ]);

  showStatus('Checking stored results...', 'loading');
  updateProgress(0, 'Checking stored results...');

  const latest = await fetchShareStateLatest(currentReplayKey);
  let foundStoredButFailedToLoad = false;
  if (latest?.available) {
    currentContentHash = latest.contentHash;
    setTimings(latest.timings);
    currentTimingAgg = latest.timingsAgg;

    const env = await fetchShareStatePayload(latest.payloadUrl);
    const data = env?.data;
    if (data) {
      showStatus('Loaded stored results. Click Regenerate to recompute.', 'success');
      setMeta([
        { k: 'Replay ID', v: String(replayId) },
        { k: 'Replay gitCommit', v: String(replay?.gitCommit ?? '(unknown)') },
        { k: 'Stored contentHash', v: String(currentContentHash) },
      ]);
      displayComputed(data);
      return;
    }
    foundStoredButFailedToLoad = true;
  }

  showStatus(foundStoredButFailedToLoad ? 'Stored result found but failed to load. Starting analysis...' : 'No stored results. Starting analysis...', 'loading');
  await analyzeReplay(replay);
}

async function regenerateCurrentReplay() {
  if (!currentReplay) return;
  showStatus('Regenerating replay...', 'loading');
  updateProgress(0, 'Regenerating replay...');
  await analyzeReplay(currentReplay);
}

async function analyzeReplay(replay: ReplayJson) {
  updateProgress(0, 'Detecting game version...');
  const version = replay.gitCommit;
  if (!version) throw new Error('Replay missing gitCommit');

  updateProgress(25, 'Loading game engine bundle...');
  const bundleInfo = await getBundleInfo(version);
  lastBundleInfo = bundleInfo;

  updateProgress(50, 'Initializing simulation...');
  if (analysisWorker) {
    try {
      analysisWorker.terminate();
    } catch {
      // ignore
    }
    analysisWorker = null;
  }
  analysisWorker = new Worker('/assets/worker.js', { type: 'module' });
  analysisWorker.onmessage = async (event) => {
    const { type, data, progress: workerProgress } = event.data;
    if (type === 'progress' && workerProgress) {
      updateProgress(50 + workerProgress.percent * 0.4, workerProgress.message);
    } else if (type === 'complete') {
      updateProgress(90, 'Finalizing results...');
      seedTimingsFromLocalRun(data);
      displayComputed(data);
      updateProgress(100, 'Analysis complete!');
      setTimeout(() => {
        showStatus('Analysis complete!', 'success');
        ($('progress') as HTMLElement).style.display = 'none';
      }, 250);

      const submitted = await submitShareState(data);
      if (submitted?.ok) {
        currentContentHash = submitted.contentHash;
        setTimings(submitted.timings);
        // Prefer server aggregation (keeps live and stored consistent).
        currentTimingAgg = submitted.timingsAgg;
        displayComputed(data);
        showStatus(submitted.storedNewResult ? 'Stored new result (timing recorded)' : 'Result unchanged (timing recorded)', 'success');
      }
    } else if (type === 'error') {
      showStatus('Analysis failed: ' + data?.message, 'error');
      ($('progress') as HTMLElement).style.display = 'none';
    }
  };
  analysisWorker.onerror = (e) => {
    showStatus('Worker error: ' + (e as any)?.message, 'error');
    ($('progress') as HTMLElement).style.display = 'none';
  };

  analysisWorker.postMessage({
    type: 'analyze',
    replay,
    bundleUrl: bundleInfo.url,
    mapsBasePath: typeof bundleInfo.mapsBaseUrl === 'string' && bundleInfo.mapsBaseUrl.length ? bundleInfo.mapsBaseUrl : '/maps/',
  });

  const gameID = replay?.info?.gameID ?? replay?.gameID ?? '';
  setMeta([
    //{ k: 'Analyzer commit', v: analyzerCommit },
    { k: 'Replay ID', v: String(gameID || '(unknown)') },
    { k: 'Replay gitCommit', v: String(version) },
    { k: 'Bundle tag', v: String(bundleInfo.tag || '(unknown)') },
    { k: 'Bundle sha', v: String(bundleInfo.sha || bundleInfo.commit || '(unknown)') },
    //{ k: 'Bundle URL', v: String(bundleInfo.url) },
    //{ k: 'Bundle source URL', v: String(bundleInfo.sourceUrl || '(n/a)') },
    //{ k: 'Manifest generatedAt', v: String(bundleInfo.manifestGeneratedAt || '(n/a)') },
  ]);
}

async function loadReplayFromId(replayId: string) {
  const clean = replayId.replace(/[^a-zA-Z0-9]/g, '');
  if (!/^[a-zA-Z0-9]{8}$/.test(clean)) throw new Error('Replay ID must be 8 characters (e.g. CkWVL6Qe)');
  showStatus('Fetching replay from OpenFront API...', 'loading');
  updateProgress(0, 'Fetching replay...');
  const res = await fetch('/api/replays/' + encodeURIComponent(clean));
  const text = await res.text();
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${text}`);
  const replay = JSON.parse(text);
  await handleReplayLoaded(replay);
}

async function init() {
  setupThemeToggle();

  // Fetch analyzer commit from /health
  try {
    const h = await fetch('/health').then((r) => r.json());
    analyzerCommit = String(h?.analyzerCommit ?? 'dev');
  } catch {
    analyzerCommit = 'dev';
  }

  const replayIdInput = $('replayIdInput') as HTMLInputElement;
  const fetchReplayBtn = $('fetchReplayBtn') as HTMLButtonElement;

  const doFetch = async () => {
    fetchReplayBtn.disabled = true;
    try {
      await loadReplayFromId(replayIdInput.value);
    } catch (err: any) {
      showStatus('Failed to fetch replay: ' + String(err?.message ?? err), 'error');
    } finally {
      fetchReplayBtn.disabled = false;
    }
  };
  fetchReplayBtn.addEventListener('click', doFetch);
  replayIdInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') doFetch();
  });

  const maybeLoadFromHash = async () => {
    const joinId = parseJoinReplayIdFromHash(window.location.hash);
    if (!joinId) return;
    if (hashJoinLoadInFlight) return;
    // Avoid reloading the same replay if already loaded.
    if (currentReplayId && joinId === currentReplayId) return;
    hashJoinLoadInFlight = true;
    try {
      replayIdInput.value = joinId;
      await doFetch();
    } finally {
      hashJoinLoadInFlight = false;
    }
  };

  window.addEventListener('hashchange', () => {
    void maybeLoadFromHash();
  });
  void maybeLoadFromHash();
}

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', (event as any).reason);
  showStatus('An unexpected error occurred', 'error');
});

init().catch((err) => {
  console.error(err);
  showStatus('Failed to initialize app', 'error');
});
